import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import CheatSheet from '../../components/admin/CheatSheet';

// 30-day pilot countdown + enrolment progress. Shows until the school is
// licensed (licensed_at) or the pilot window is long past.
const PilotTracker = ({ school, studentTotal, onInvite }) => {
  if (!school || school.licensed_at) return null;
  const start = school.beta_start || school.created_at;
  if (!start) return null;
  const startMs = new Date(start).getTime();
  const endMs = school.beta_end ? new Date(school.beta_end).getTime() : startMs + 14 * 86400000;
  if (Date.now() - endMs > 30 * 86400000) return null;
  const totalDays = Math.max(1, Math.round((endMs - startMs) / 86400000));
  const daysLeft = Math.max(0, Math.ceil((endMs - Date.now()) / 86400000));
  const expired = daysLeft === 0;
  const target = school.beta_student_limit || Math.min(school.student_count_estimate || 10, 10);
  const daysPct = Math.min(100, Math.round(((totalDays - daysLeft) / totalDays) * 100));
  const enrolPct = target ? Math.min(100, Math.round(((studentTotal || 0) / target) * 100)) : 0;

  return (
    <div className="bg-white rounded-2xl border p-5 mb-6" style={{ borderColor: expired ? '#FCA5A5' : '#F3E8D9' }} data-testid="pilot-tracker">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest" style={{ color: '#F5831F' }}>{totalDays}-Day Pilot</p>
          <p className="text-lg font-bold" style={{ color: '#1A1209' }} data-testid="pilot-days-left">
            {expired ? 'Your pilot has ended' : `${daysLeft} day${daysLeft === 1 ? '' : 's'} left`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onInvite} className="px-4 py-2 text-sm font-semibold text-white rounded-lg" style={{ background: '#7B4FD8' }} data-testid="pilot-invite-btn">
            Invite students
          </button>
          <a href="/schools#pricing" className="px-4 py-2 text-sm font-semibold rounded-lg border" style={{ color: '#7B4FD8', borderColor: '#7B4FD8' }} data-testid="pilot-license-btn">
            Licence your school
          </a>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <div className="flex justify-between text-xs mb-1" style={{ color: '#8A8073' }}>
            <span>Pilot progress</span><span>{daysPct}%</span>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ background: '#F3E8DC' }}>
            <div className="h-full rounded-full" style={{ width: `${daysPct}%`, background: expired ? '#EF4444' : 'linear-gradient(90deg, #F5831F, #FFB073)' }} />
          </div>
        </div>
        <div>
          <div className="flex justify-between text-xs mb-1" style={{ color: '#8A8073' }}>
            <span>Students signed up</span>
            <span data-testid="pilot-enrolment-count">{studentTotal || 0} of {target}</span>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ background: '#F3E8DC' }}>
            <div className="h-full rounded-full" style={{ width: `${enrolPct}%`, background: 'linear-gradient(90deg, #7B4FD8, #A78BFA)' }} />
          </div>
        </div>
      </div>
      {expired && (
        <p className="text-xs mt-3" style={{ color: '#B91C1C' }}>
          Keep your students' progress — licence your school to continue without interruption.
        </p>
      )}
    </div>
  );
};
import {
  Users, GraduationCap, BarChart3, Upload, Plus, Search, Trash2, Edit2,
  Check, X, Loader2, LogOut, School, AlertTriangle, Key, Copy, RefreshCw,
  Clock, Mail, FileText, ChevronDown, Settings, Image, BookOpen, Heart, Menu,
  HelpCircle, Activity, Zap, Award, Shield, Send, ChevronUp, Bell, Trophy,
  Download, UserPlus, Calendar, Palette, TrendingUp, UserCheck, CheckCircle, XCircle,
  Eye, ArrowLeft
} from 'lucide-react';
import SchoolOnboardingWizard from './SchoolOnboardingWizard';
import LeaderboardTab from './LeaderboardTab';
import LicenseCard from './LicenseCard';
import DashboardCover from '../../components/DashboardCover';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SchoolDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [school, setSchool] = useState(null);
  const [summary, setSummary] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [usage, setUsage] = useState(null);
  const [students, setStudents] = useState([]);
  const [studentTotal, setStudentTotal] = useState(0);
  const [accessCodes, setAccessCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gradeFilter, setGradeFilter] = useState('');
  const [teacherFilter, setTeacherFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [addForm, setAddForm] = useState({ first_name: '', last_name: '', grade: '', section: '', teacher_name: '', gender: '', parent_email: '', parent_name: '' });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [editingStudent, setEditingStudent] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [regenerating, setRegenerating] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const [reports, setReports] = useState([]);
  const [pastoral, setPastoral] = useState(null);
  const [oceanAggregates, setOceanAggregates] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const logoFileRef = useRef(null);
  const fileRef = useRef(null);
  const [showWizard, setShowWizard] = useState(false);
  const [wizardChecked, setWizardChecked] = useState(false);
  const [hasSkippedSteps, setHasSkippedSteps] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [usageDetail, setUsageDetail] = useState(null);
  const [schoolSettings, setSchoolSettings] = useState({ nft_rewards_enabled: true, nudge_enabled: true, nudge_inactive_days: 3, max_daily_usage_minutes: 60 });
  const [guide, setGuide] = useState(null);
  const [guideOpen, setGuideOpen] = useState({});
  const [leaderboard, setLeaderboard] = useState([]);
  const [nudging, setNudging] = useState(false);
  const [nudgeResult, setNudgeResult] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgForm, setMsgForm] = useState({ subject: '', message: '', category: 'general' });
  const [sendingMsg, setSendingMsg] = useState(false);
  const [msgSent, setMsgSent] = useState(false);

  // New enhancement states
  const [alerts, setAlerts] = useState([]);
  const [alertSummary, setAlertSummary] = useState({ urgent: 0, concern: 0, watch: 0 });
  const [benchmarks, setBenchmarks] = useState(null);
  const [certificates, setCertificates] = useState(null);
  const [staff, setStaff] = useState([]);
  const [availableClasses, setAvailableClasses] = useState([]);
  const [showInviteStaff, setShowInviteStaff] = useState(false);
  const [staffForm, setStaffForm] = useState({ email: '', name: '', role: 'teacher', classes: [] });
  const [invitingStaff, setInvitingStaff] = useState(false);
  const [calendarTerms, setCalendarTerms] = useState([]);
  const [inHoliday, setInHoliday] = useState(false);
  const [showAddTerm, setShowAddTerm] = useState(false);
  const [termForm, setTermForm] = useState({ name: '', start_date: '', end_date: '', type: 'term' });
  const [scheduleSlots, setScheduleSlots] = useState([]);
  const [showAddSlot, setShowAddSlot] = useState(false);
  const [slotForm, setSlotForm] = useState({ day: 'Monday', start_time: '09:00', end_time: '09:30', grade: '', label: 'Practice Session' });
  const [branding, setBranding] = useState({ primary_color: '#F5831F', tagline: '' });
  const [parentReportHistory, setParentReportHistory] = useState([]);
  const [sendingParentReports, setSendingParentReports] = useState(false);
  const [parentReportResult, setParentReportResult] = useState(null);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [bulkAction, setBulkAction] = useState(null);
  const [leaderboardData, setLeaderboardData] = useState(null);
  const [leaderboardOptIn, setLeaderboardOptIn] = useState(true);

  const getToken = () => localStorage.getItem('hibff_auth_token') || sessionStorage.getItem('hibff_auth_token');

  const US_GRADES = ['6th', '7th', '8th', '9th', '10th', '11th', '12th'];

  const fetchData = useCallback(async () => {
    const token = getToken();
    if (!token) { setLoading(false); return; }
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [schoolRes, summaryRes, dashRes, usageRes, codesRes, reportsRes, pastoralRes, oceanRes, usageDetailRes, settingsRes, guideRes, leaderboardRes] = await Promise.all([
        fetch(`${API_URL}/api/school/info`, { headers }),
        fetch(`${API_URL}/api/school/roster-summary`, { headers }),
        fetch(`${API_URL}/api/school/dashboard`, { headers }),
        fetch(`${API_URL}/api/school/usage`, { headers }),
        fetch(`${API_URL}/api/school/access-codes`, { headers }),
        fetch(`${API_URL}/api/reports/school`, { headers }),
        fetch(`${API_URL}/api/school/pastoral`, { headers }),
        fetch(`${API_URL}/api/ocean/school-aggregates`, { headers }),
        fetch(`${API_URL}/api/school/usage-detail`, { headers }),
        fetch(`${API_URL}/api/school/settings`, { headers }),
        fetch(`${API_URL}/api/school/guide`, { headers }),
        fetch(`${API_URL}/api/school/leaderboard`, { headers }),
      ]);
      if (schoolRes.ok) setSchool(await schoolRes.json());
      if (summaryRes.ok) setSummary(await summaryRes.json());
      if (dashRes.ok) setDashboard(await dashRes.json());
      if (usageRes.ok) setUsage(await usageRes.json());
      if (codesRes.ok) {
        const cData = await codesRes.json();
        setAccessCodes(cData.codes || []);
      }
      if (reportsRes.ok) {
        const rData = await reportsRes.json();
        setReports(rData.reports || []);
      }
      if (pastoralRes.ok) setPastoral(await pastoralRes.json());
      if (oceanRes.ok) setOceanAggregates(await oceanRes.json());
      if (usageDetailRes.ok) setUsageDetail(await usageDetailRes.json());
      if (settingsRes.ok) setSchoolSettings(await settingsRes.json());
      if (guideRes.ok) setGuide(await guideRes.json());
      if (leaderboardRes.ok) {
        const lb = await leaderboardRes.json();
        setLeaderboard(lb.leaderboard || []);
      }
      // Fetch messages
      try {
        const msgRes = await fetch(`${API_URL}/api/school/messages/my`, { headers });
        if (msgRes.ok) { const md = await msgRes.json(); setMessages(md.messages || []); }
      } catch (err) { /* handled */}
      // Fetch enhancement data
      try {
        const [alertsRes, benchRes, certsRes, staffRes, calRes, schedRes, brandRes, prHistRes] = await Promise.all([
          fetch(`${API_URL}/api/school/wellbeing/alerts`, { headers }),
          fetch(`${API_URL}/api/school/benchmarks`, { headers }),
          fetch(`${API_URL}/api/school/certificates`, { headers }),
          fetch(`${API_URL}/api/school/staff`, { headers }),
          fetch(`${API_URL}/api/school/calendar`, { headers }),
          fetch(`${API_URL}/api/school/schedule`, { headers }),
          fetch(`${API_URL}/api/school/branding`, { headers }),
          fetch(`${API_URL}/api/school/reports/parent-history`, { headers }),
        ]);
        if (alertsRes.ok) { const ad = await alertsRes.json(); setAlerts(ad.alerts || []); setAlertSummary(ad.summary || {}); }
        if (benchRes.ok) setBenchmarks(await benchRes.json());
        if (certsRes.ok) setCertificates(await certsRes.json());
        if (staffRes.ok) { const sd = await staffRes.json(); setStaff(sd.staff || []); setAvailableClasses(sd.available_classes || []); }
        if (calRes.ok) { const cd = await calRes.json(); setCalendarTerms(cd.terms || []); setInHoliday(cd.in_holiday || false); }
        if (schedRes.ok) { const sd = await schedRes.json(); setScheduleSlots(sd.slots || []); }
        if (brandRes.ok) setBranding(await brandRes.json());
        if (prHistRes.ok) { const ph = await prHistRes.json(); setParentReportHistory(ph.reports || []); }
      } catch (err) { /* handled */}
      // Fetch leaderboard lazily (not in initial load)
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  const fetchStudents = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    const params = new URLSearchParams();
    if (gradeFilter) params.append('grade', gradeFilter);
    if (teacherFilter) params.append('teacher', teacherFilter);
    if (searchQuery) params.append('search', searchQuery);
    try {
      const res = await fetch(`${API_URL}/api/school/students?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setStudents(data.students || []);
        setStudentTotal(data.total || 0);
      }
    } catch (e) { console.error(e); }
  }, [gradeFilter, teacherFilter, searchQuery]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { if (school?.status === 'active') fetchStudents(); }, [fetchStudents, school?.status]);

  // Check wizard status on first load
  useEffect(() => {
    if (wizardChecked || !school || school.status !== 'active') return;
    setWizardChecked(true);
    const token = getToken();
    if (!token) return;
    fetch(`${API_URL}/api/school/wizard/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          if (data.show_wizard) setShowWizard(true);
          if (data.has_skipped_steps) setHasSkippedSteps(true);
        }
      })
      .catch(() => { /* handled */ });
  }, [school, wizardChecked]);

  const addStudent = async () => {
    if (!addForm.first_name.trim() || !addForm.last_name.trim()) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/school/students`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify(addForm),
      });
      if (res.ok) {
        setAddForm({ first_name: '', last_name: '', grade: '', section: '', teacher_name: '', gender: '', parent_email: '', parent_name: '' });
        setShowAddForm(false);
        fetchStudents();
        fetchData();
      }
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_URL}/api/school/students/bulk`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      const data = await res.json();
      setUploadResult(data);
      if (data.added > 0) { fetchStudents(); fetchData(); }
    } catch (e) { console.error(e); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const deleteStudent = async (studentId, name) => {
    if (!window.confirm(`Remove ${name} from the roster?`)) return;
    try {
      await fetch(`${API_URL}/api/school/students/${studentId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      fetchStudents();
      fetchData();
    } catch (e) { console.error(e); }
  };

  const startEdit = (s) => {
    setEditingStudent(s.student_id);
    setEditForm({ first_name: s.first_name, last_name: s.last_name, grade: s.grade, section: s.section, teacher_name: s.teacher_name, gender: s.gender, parent_email: s.parent_email || '', parent_name: s.parent_name || '' });
  };

  const saveEdit = async (studentId) => {
    try {
      await fetch(`${API_URL}/api/school/students/${studentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify(editForm),
      });
      setEditingStudent(null);
      fetchStudents();
    } catch (e) { console.error(e); }
  };

  const regenerateCode = async () => {
    setRegenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/school/access-codes/regenerate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (res.ok) fetchData();
    } catch (e) { console.error(e); }
    setRegenerating(false);
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCodeCopied(true);
    setTimeout(() => setCodeCopied(false), 2000);
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F8F7F4' }}>
      <Loader2 className="animate-spin" size={32} style={{ color: '#F5831F' }} />
    </div>
  );

  /* ─── PENDING APPROVAL SCREEN ──────────────────────────────────────────── */
  if (school && school.status === 'pending_approval') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#F8F7F4' }}>
        <div className="max-w-lg w-full" data-testid="pending-approval-screen">
          <div className="bg-white rounded-2xl overflow-hidden" style={{ border: '1px solid #E8E8E8' }}>
            {/* Header bar */}
            <div className="px-8 py-5" style={{ backgroundColor: '#1A1209' }}>
              <div className="flex items-center gap-2">
                <School size={20} style={{ color: '#F5831F' }} />
                <span className="text-sm font-bold text-white">HiBFF Schools</span>
              </div>
            </div>

            <div className="p-4 sm:p-8">
              {/* Status badge */}
              <div className="flex items-center gap-2 mb-6">
                <div className="w-2.5 h-2.5 rounded-full animate-pulse" style={{ backgroundColor: '#F5831F' }} />
                <span className="text-sm font-semibold" style={{ color: '#F5831F' }} data-testid="status-badge">Under Review</span>
              </div>

              <h1 className="text-xl font-bold mb-2" style={{ color: '#1A1209' }}>
                Welcome, {school.contact_name}
              </h1>
              <p className="text-sm mb-6" style={{ color: '#888' }}>
                Your application for <strong style={{ color: '#1A1209' }}>{school.school_name}</strong> has been received and is being reviewed by our team.
              </p>

              {/* Application details */}
              <div className="rounded-xl p-5 mb-6" style={{ backgroundColor: '#F8F7F4' }}>
                <h3 className="text-xs font-semibold uppercase tracking-wider mb-4" style={{ color: '#999' }}>Application Details</h3>
                <div className="space-y-3">
                  <DetailRow icon={<FileText size={14} />} label="School" value={school.school_name} />
                  <DetailRow icon={<Clock size={14} />} label="Submitted" value={new Date(school.submitted_at || school.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })} />
                  <DetailRow icon={<AlertTriangle size={14} />} label="Status" value="Under Review" valueColor="#F5831F" />
                  <DetailRow icon={<Mail size={14} />} label="Contact Email" value={school.admin_email} />
                </div>
              </div>

              {/* Timeline */}
              <div className="rounded-xl p-5 mb-6" style={{ backgroundColor: '#EFF6FF', border: '1px solid #BFDBFE' }}>
                <h3 className="text-sm font-semibold mb-3" style={{ color: '#1E40AF' }}>What happens next</h3>
                <ol className="space-y-2.5">
                  <TimelineStep num="1" text="Our team reviews your application (within 48 hours)" active />
                  <TimelineStep num="2" text="You receive an activation email with login details and next steps" />
                  <TimelineStep num="3" text="Upload your student roster or share a parent enrollment code" />
                  <TimelineStep num="4" text="Parents activate student accounts — students begin building social confidence" />
                </ol>
              </div>

              <p className="text-xs text-center" style={{ color: '#BBB' }}>
                Questions? Reach us at <a href="mailto:support@hibff.com" className="underline" style={{ color: '#7B4FD8' }}>support@hibff.com</a>
              </p>
            </div>

            {/* Footer */}
            <div className="px-8 py-4 flex justify-between items-center" style={{ borderTop: '1px solid #F0F0F0' }}>
              <span className="text-xs" style={{ color: '#CCC' }}>Logged in as {user?.email}</span>
              <button onClick={logout} className="flex items-center gap-1.5 text-xs font-medium" style={{ color: '#999' }} data-testid="pending-logout-btn">
                <LogOut size={12} /> Log Out
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ─── ACTIVE DASHBOARD ─────────────────────────────────────────────────── */
  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'leaderboard', label: 'Leaderboard', icon: TrendingUp },
    { id: 'engagement', label: 'Engagement', icon: Activity },
    { id: 'pastoral', label: 'Pastoral', icon: Heart },
    { id: 'certificates', label: 'Certificates', icon: Award },
    { id: 'students', label: 'Student Roster', icon: Users },
    { id: 'staff', label: 'Teachers & Staff', icon: UserCheck },
    { id: 'access-codes', label: 'Access Codes', icon: Key },
    { id: 'reports', label: 'Reports', icon: FileText },
    { id: 'messages', label: 'Contact HiBFF', icon: Mail },
    { id: 'settings', label: 'Settings', icon: Settings },
    { id: 'guide', label: 'How-To Guide', icon: HelpCircle },
    { id: 'product-docs', label: 'Product Docs', icon: FileText },
  ];

  const activeCode = accessCodes.find(c => c.active);
  const maxUsage = usage?.weekly_usage ? Math.max(...usage.weekly_usage.map(w => w.sessions), 1) : 1;

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: '#F8F7F4' }}>
      {/* Dashboard Cover */}
      <DashboardCover
        orgName={school?.school_name}
        logoUrl={school?.logo_url}
        subtitle="School Dashboard"
        accentColor="#4E6B52"
        onSettingsClick={() => { setTab('settings'); }}
      />

      {/* Mobile overlay */}
      {mobileSidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-30 md:hidden" onClick={() => setMobileSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed md:static top-0 left-0 h-full md:h-auto w-56 flex flex-col z-40 transition-transform duration-300 ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`} style={{ backgroundColor: '#4E6B52' }}>
        {/* Mobile close */}
        <button onClick={() => setMobileSidebarOpen(false)} className="md:hidden absolute top-4 right-3 text-white/60 hover:text-white z-50" aria-label="Close">
          <X size={22} />
        </button>

        <div className="p-5 pb-3">
          <div className="flex items-center gap-2 mb-1">
            <School size={20} style={{ color: '#A8D8A0' }} />
            <span className="text-sm font-bold text-white">HiBFF Schools</span>
          </div>
          {school && <p className="text-xs truncate" style={{ color: '#C8D5CA' }}>{school.school_name}</p>}
        </div>
        <nav className="flex-1 px-3">
          {tabs.map(t => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button key={t.id} onClick={() => { setTab(t.id); if (window.innerWidth < 768) setMobileSidebarOpen(false); }}
                className="w-full flex items-center gap-2.5 px-3 py-2.5 mb-1 rounded-lg text-sm transition-all"
                style={active ? { backgroundColor: '#F5831F22', color: '#F5831F' } : { color: '#C8D5CA' }}
                data-testid={`tab-${t.id}`}>
                <Icon size={16} />
                {t.label}
              </button>
            );
          })}
        </nav>
        <div className="p-3 mt-auto" style={{ borderTop: '1px solid #5A7B5E' }}>
          {/* Setup Guide button */}
          <button
            onClick={() => setShowWizard(true)}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 mb-2 rounded-lg text-sm transition-all"
            style={{ color: '#C8D5CA' }}
            data-testid="setup-guide-btn"
          >
            <div className="relative">
              <BookOpen size={16} />
              {hasSkippedSteps && (
                <div className="absolute -top-1 -right-1 w-2 h-2 rounded-full" style={{ backgroundColor: '#F5831F' }} data-testid="setup-guide-dot" />
              )}
            </div>
            Setup Guide
          </button>
          <div className="flex items-center gap-2 px-2 py-2">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ backgroundColor: '#F5831F' }}>
              {user?.name?.[0] || 'S'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-white truncate">{user?.name}</p>
              <p className="text-[10px]" style={{ color: '#C8D5CA' }}>School Admin</p>
            </div>
            <button onClick={logout} className="p-1 rounded" title="Logout" data-testid="logout-btn">
              <LogOut size={14} style={{ color: '#C8D5CA' }} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto p-4 sm:p-6">
        {/* Mobile header */}
        <div className="flex items-center gap-3 mb-4 md:hidden">
          <button onClick={() => setMobileSidebarOpen(true)} className="p-1.5 rounded-lg hover:bg-gray-100" aria-label="Open menu" data-testid="school-mobile-menu">
            <Menu size={22} style={{ color: '#1A1209' }} />
          </button>
          <div className="flex items-center gap-2">
            <School size={18} style={{ color: '#F5831F' }} />
            <span className="text-sm font-bold" style={{ color: '#1A1209' }}>HiBFF Schools</span>
          </div>
        </div>
        {/* ── Overview ─────────────────────────────────────────────── */}
        <CheatSheet pageId="school-dashboard" />
        {tab === 'overview' && (
          <div data-testid="overview-tab">
            <PilotTracker school={school} studentTotal={studentTotal} onInvite={() => setTab('access-codes')} />
            <div className="flex items-center justify-between mb-1">
              <h1 className="text-xl font-bold" style={{ color: '#1A1209' }}>
                {school?.school_name || 'School'} Dashboard
              </h1>
              <button onClick={() => navigate('/school/student-preview')}
                className="px-4 py-2 rounded-xl flex items-center gap-2 group transition-all hover:scale-[1.02]"
                style={{ background: 'linear-gradient(135deg, #1A1209 0%, #3D2E1A 100%)', border: '1px solid #F5831F33' }}
                data-testid="preview-student-btn">
                <Eye size={14} style={{ color: '#F5831F' }} />
                <span className="text-xs font-semibold" style={{ color: '#E8DDD0' }}>Preview Student Experience</span>
                <ArrowLeft size={11} className="rotate-180" style={{ color: '#F5831F' }} />
              </button>
            </div>
            <p className="text-xs mb-5" style={{ color: '#999' }}>
              {school?.status === 'active' && school?.beta_end && `Free beta — expires ${new Date(school.beta_end).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`}
            </p>

            <LicenseCard />

            {/* Privacy banner */}
            <div className="flex items-center gap-2 px-4 py-2.5 mb-5 rounded-lg text-xs" style={{ backgroundColor: '#EFF6FF', color: '#2563EB', border: '1px solid #BFDBFE' }} data-testid="privacy-banner">
              <AlertTriangle size={14} />
              <span>This dashboard shows <strong>aggregated metrics only</strong>. Individual student conversations are never accessible.</span>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
              <StatCard label="Total Students" value={summary?.total || 0} icon={<Users size={18} style={{ color: '#F5831F' }} />} />
              <StatCard label="Grades" value={summary?.by_grade?.length || 0} icon={<GraduationCap size={18} style={{ color: '#2563EB' }} />} />
              <StatCard label="Teachers" value={summary?.by_teacher?.length || 0} icon={<School size={18} style={{ color: '#7B4FD8' }} />} />
              <StatCard label="Sessions This Week" value={dashboard?.active_sessions_this_week || 0} icon={<BarChart3 size={18} style={{ color: '#2EAA5C' }} />} />
            </div>

            {/* Charts row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              {/* Grade Distribution */}
              <div className="bg-white rounded-xl p-4" style={{ border: '1px solid #E8E8E8' }}>
                <h3 className="text-sm font-bold mb-3" style={{ color: '#1A1209' }}>Students by Grade</h3>
                {summary?.by_grade?.length > 0 ? (
                  <div className="space-y-2">
                    {summary.by_grade.map(g => (
                      <div key={g.grade} className="flex items-center gap-2">
                        <span className="text-xs w-20 text-right font-medium" style={{ color: '#666' }}>{g.grade}</span>
                        <div className="flex-1 h-5 rounded-full overflow-hidden" style={{ backgroundColor: '#F0F0F0' }}>
                          <div className="h-full rounded-full" style={{ width: `${(g.count / (summary.total || 1)) * 100}%`, backgroundColor: '#F5831F' }} />
                        </div>
                        <span className="text-xs font-bold w-8" style={{ color: '#333' }}>{g.count}</span>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-xs" style={{ color: '#AAA' }}>No students enrolled yet</p>}
              </div>

              {/* Weekly Usage */}
              <div className="bg-white rounded-xl p-4" style={{ border: '1px solid #E8E8E8' }}>
                <h3 className="text-sm font-bold mb-3" style={{ color: '#1A1209' }}>Weekly Usage (Sessions)</h3>
                {usage?.weekly_usage?.length > 0 ? (
                  <div className="flex items-end gap-3 h-32">
                    {usage.weekly_usage.map((w, i) => (
                      <div key={i} className="flex-1 flex flex-col items-center gap-1">
                        <span className="text-[10px] font-bold" style={{ color: '#333' }}>{w.sessions}</span>
                        <div className="w-full rounded-t-md" style={{ height: `${(w.sessions / maxUsage) * 100}%`, minHeight: 4, backgroundColor: '#2563EB' }} />
                        <span className="text-[10px]" style={{ color: '#999' }}>{w.week}</span>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-xs" style={{ color: '#AAA' }}>No usage data yet</p>}
              </div>
            </div>

            {/* Teacher summary */}
            {summary?.by_teacher?.length > 0 && (
              <div className="bg-white rounded-xl p-4" style={{ border: '1px solid #E8E8E8' }}>
                <h3 className="text-sm font-bold mb-3" style={{ color: '#1A1209' }}>Students by Teacher</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {summary.by_teacher.map(t => (
                    <div key={t.teacher} className="flex items-center justify-between px-3 py-2 rounded-lg" style={{ backgroundColor: '#F8F7F4' }}>
                      <span className="text-xs font-medium" style={{ color: '#333' }}>{t.teacher}</span>
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#F5831F15', color: '#F5831F' }}>{t.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Leaderboard + Nudge */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Leaderboard */}
              {leaderboard.length > 0 && (
                <div className="bg-white rounded-xl p-4" style={{ border: '1px solid #E8E8E8' }} data-testid="leaderboard-card">
                  <h3 className="text-sm font-bold mb-3 flex items-center gap-2" style={{ color: '#1A1209' }}>
                    <Trophy size={14} style={{ color: '#F5831F' }} /> Top Achievers This Month
                  </h3>
                  <div className="space-y-1.5">
                    {leaderboard.slice(0, 5).map((s, i) => (
                      <div key={i} className="flex items-center gap-3 px-3 py-2 rounded-lg" style={{ backgroundColor: i < 3 ? ['#FFF7ED', '#F0F9FF', '#FEF3C7'][i] : '#F8F7F4' }}>
                        <span className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black" style={{
                          backgroundColor: i < 3 ? ['#F5831F', '#3B82F6', '#F59E0B'][i] : '#DDD',
                          color: i < 3 ? 'white' : '#999',
                        }}>{i + 1}</span>
                        <div className="flex-1">
                          <span className="text-xs font-semibold" style={{ color: '#1A1209' }}>{s.first_name}</span>
                          <span className="text-[10px] ml-2" style={{ color: '#999' }}>{s.grade}</span>
                        </div>
                        <div className="flex gap-3 text-[10px]" style={{ color: '#888' }}>
                          <span>{s.challenges} challenges</span>
                          <span>{s.sessions} sessions</span>
                        </div>
                        <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#F5831F15', color: '#F5831F' }}>{s.score}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Nudge Panel */}
              <div className="bg-white rounded-xl p-4" style={{ border: '1px solid #E8E8E8' }} data-testid="nudge-card">
                <h3 className="text-sm font-bold mb-2 flex items-center gap-2" style={{ color: '#1A1209' }}>
                  <Bell size={14} style={{ color: '#2563EB' }} /> Student Nudges
                </h3>
                <p className="text-xs mb-3" style={{ color: '#999' }}>
                  Send friendly reminders to students who haven&apos;t been active in {schoolSettings.nudge_inactive_days} days.
                  Adjust the threshold in Settings.
                </p>
                <button
                  onClick={async () => {
                    setNudging(true); setNudgeResult(null);
                    try {
                      const res = await fetch(`${API_URL}/api/school/nudge`, {
                        method: 'POST', headers: { Authorization: `Bearer ${getToken()}` },
                      });
                      if (res.ok) setNudgeResult(await res.json());
                    } catch (err) { /* handled */}
                    setNudging(false);
                  }}
                  disabled={nudging}
                  className="w-full py-2.5 rounded-lg text-sm font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-50"
                  style={{ backgroundColor: '#2563EB' }}
                  data-testid="send-nudge-btn"
                >
                  {nudging ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  {nudging ? 'Sending...' : 'Send Nudges to Inactive Students'}
                </button>
                {nudgeResult && (
                  <div className="mt-3 p-3 rounded-lg" style={{ backgroundColor: nudgeResult.nudged_count > 0 ? '#ECFDF5' : '#F0F9FF' }}>
                    <p className="text-xs font-semibold" style={{ color: nudgeResult.nudged_count > 0 ? '#065F46' : '#1E40AF' }}>
                      {nudgeResult.nudged_count > 0 ? `Nudged ${nudgeResult.nudged_count} students` : 'All students are active — no nudges needed!'}
                    </p>
                    {nudgeResult.nudged_students?.slice(0, 5).map((s, i) => (
                      <span key={i} className="text-[10px] mr-2" style={{ color: '#059669' }}>{s.name}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Benchmarks */}
            {benchmarks && (
              <div className="bg-white rounded-xl p-5 mt-5" style={{ border: '1px solid #E8E8E8' }} data-testid="benchmarks-card">
                <h3 className="text-sm font-bold mb-4 flex items-center gap-2" style={{ color: '#1A1209' }}>
                  <TrendingUp size={14} style={{ color: '#8B5CF6' }} /> How You Compare (Platform Benchmarks)
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { label: 'Sessions / Student (30d)', yours: benchmarks.school?.sessions_per_student, platform: benchmarks.platform?.sessions_per_student, delta: benchmarks.deltas?.sessions },
                    { label: 'Challenges / Student', yours: benchmarks.school?.challenges_per_student, platform: benchmarks.platform?.challenges_per_student, delta: benchmarks.deltas?.challenges },
                  ].map(b => (
                    <div key={b.label} className="p-4 rounded-xl" style={{ backgroundColor: '#F8F7F4' }}>
                      <p className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: '#999' }}>{b.label}</p>
                      <div className="flex items-end gap-4">
                        <div>
                          <p className="text-2xl font-bold" style={{ color: '#1A1209' }}>{b.yours}</p>
                          <p className="text-[10px]" style={{ color: '#999' }}>Your school</p>
                        </div>
                        <div>
                          <p className="text-lg font-semibold" style={{ color: '#999' }}>{b.platform}</p>
                          <p className="text-[10px]" style={{ color: '#CCC' }}>Platform avg</p>
                        </div>
                        {b.delta !== 0 && (
                          <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{
                            backgroundColor: b.delta > 0 ? '#ECFDF5' : b.delta < 0 ? '#FEF2F2' : '#F0F0F0',
                            color: b.delta > 0 ? '#059669' : b.delta < 0 ? '#DC2626' : '#999',
                          }}>
                            {b.delta > 0 ? '+' : ''}{b.delta}%
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-[10px] mt-3" style={{ color: '#CCC' }}>
                  Compared across {benchmarks.platform?.total_schools} schools, {benchmarks.platform?.total_students} students
                </p>
              </div>
            )}

            {/* Academic Calendar Status */}
            {inHoliday && (
              <div className="mt-5 flex items-center gap-3 px-4 py-3 rounded-xl" style={{ backgroundColor: '#FFFBEB', border: '1px solid #FEF3C7' }} data-testid="holiday-banner">
                <Calendar size={16} style={{ color: '#F59E0B' }} />
                <p className="text-xs font-medium" style={{ color: '#92400E' }}>
                  School is currently on holiday. Nudges and inactivity alerts are paused.
                </p>
              </div>
            )}
          </div>
        )}

        {/* ── Leaderboard ─────────────────────────────────────── */}
        {tab === 'leaderboard' && (
          <LeaderboardTab
            API_URL={API_URL} getToken={getToken} leaderboardData={leaderboardData}
            setLeaderboardData={setLeaderboardData} leaderboardOptIn={leaderboardOptIn}
            setLeaderboardOptIn={setLeaderboardOptIn} school={school}
          />
        )}

        {/* ── Engagement (Usage Detail) ─────────────────────────────── */}
        {tab === 'engagement' && (
          <div data-testid="engagement-tab">
            <h1 className="text-xl font-bold mb-1" style={{ color: '#1A1209' }}>Student Engagement</h1>
            <p className="text-xs mb-5" style={{ color: '#999' }}>
              Per-student usage breakdown. Track time spent, sessions, challenges completed, and identify students who may need encouragement.
            </p>

            {/* Export button */}
            <div className="flex justify-end mb-3">
              <button
                onClick={() => {
                  const token = getToken();
                  fetch(`${API_URL}/api/school/export/engagement`, { headers: { Authorization: `Bearer ${token}` } })
                    .then(r => r.blob()).then(b => { const u = URL.createObjectURL(b); const a = document.createElement('a'); a.href = u; a.download = 'engagement_export.csv'; a.click(); URL.revokeObjectURL(u); });
                }}
                className="px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5"
                style={{ backgroundColor: '#F0F0F0', color: '#555' }} data-testid="export-engagement-csv"
              >
                <Download size={12} /> Export CSV
              </button>
            </div>

            {/* Summary stats */}
            {usageDetail?.students?.length > 0 && (() => {
              const sts = usageDetail.students;
              const totalMins = sts.reduce((s, x) => s + x.total_minutes_30d, 0);
              const totalSessions = sts.reduce((s, x) => s + x.sessions_30d, 0);
              const totalChallenges = sts.reduce((s, x) => s + x.challenges_completed, 0);
              const activeCount = sts.filter(x => x.days_inactive === null || x.days_inactive < 7).length;
              return (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                  <StatCard label="Total Usage (30d)" value={`${Math.round(totalMins / 60)}h ${totalMins % 60}m`} icon={<Clock size={14} style={{ color: '#2563EB' }} />} />
                  <StatCard label="Total Sessions" value={totalSessions} icon={<Activity size={14} style={{ color: '#F5831F' }} />} />
                  <StatCard label="Challenges Done" value={totalChallenges} icon={<Award size={14} style={{ color: '#10B981' }} />} />
                  <StatCard label="Active Students" value={`${activeCount} / ${sts.length}`} icon={<Zap size={14} style={{ color: '#8B5CF6' }} />} />
                </div>
              );
            })()}

            {/* Usage table */}
            {usageDetail?.students?.length > 0 ? (
              <div className="bg-white rounded-xl overflow-hidden" style={{ border: '1px solid #E8E8E8' }}>
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr style={{ backgroundColor: '#1A1209' }}>
                        {['Student', 'Grade', 'Time (30d)', 'Time (7d)', 'Sessions', 'Challenges', 'Skills', 'Avg / Session', 'Last Active', 'Status'].map(h => (
                          <th key={h} className="px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-white/60">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {usageDetail.students.map((s, i) => {
                        const status = s.days_inactive === null ? 'new' :
                          s.days_inactive === 0 ? 'active' :
                          s.days_inactive <= 3 ? 'recent' :
                          s.days_inactive <= 7 ? 'fading' : 'inactive';
                        const statusStyle = {
                          active: { bg: '#ECFDF5', color: '#065F46', label: 'Active' },
                          recent: { bg: '#F0FDF4', color: '#15803D', label: 'Recent' },
                          fading: { bg: '#FFF7ED', color: '#9A3412', label: 'Fading' },
                          inactive: { bg: '#FEF2F2', color: '#991B1B', label: 'Inactive' },
                          new: { bg: '#F0F9FF', color: '#1E40AF', label: 'New' },
                        }[status];
                        return (
                          <tr key={s.student_id} className="border-b" style={{ borderColor: '#F3F4F6', backgroundColor: i % 2 === 0 ? '#FAFAFA' : 'white' }}>
                            <td className="px-3 py-2.5 text-xs font-semibold" style={{ color: '#1A1209' }}>{s.first_name} {s.last_initial}</td>
                            <td className="px-3 py-2.5"><span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#2563EB15', color: '#2563EB' }}>{s.grade}</span></td>
                            <td className="px-3 py-2.5 text-xs font-semibold" style={{ color: '#1A1209' }}>{s.total_minutes_30d}m</td>
                            <td className="px-3 py-2.5 text-xs" style={{ color: '#666' }}>{s.minutes_7d}m</td>
                            <td className="px-3 py-2.5 text-xs" style={{ color: '#666' }}>{s.sessions_30d}</td>
                            <td className="px-3 py-2.5 text-xs font-semibold" style={{ color: '#F5831F' }}>{s.challenges_completed}</td>
                            <td className="px-3 py-2.5 text-xs" style={{ color: '#666' }}>{s.skills_practiced}</td>
                            <td className="px-3 py-2.5 text-xs" style={{ color: '#666' }}>{s.avg_session_min}m</td>
                            <td className="px-3 py-2.5 text-xs" style={{ color: '#999' }}>
                              {s.last_active ? (() => {
                                const d = s.days_inactive;
                                if (d === 0) return 'Today';
                                if (d === 1) return 'Yesterday';
                                if (d < 7) return `${d}d ago`;
                                return new Date(s.last_active).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
                              })() : 'Never'}
                            </td>
                            <td className="px-3 py-2.5">
                              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: statusStyle.bg, color: statusStyle.color }}>
                                {statusStyle.label}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl p-8 text-center" style={{ border: '1px solid #E8E8E8' }}>
                <Activity size={32} style={{ color: '#DDD' }} className="mx-auto mb-3" />
                <p className="text-sm" style={{ color: '#999' }}>No engagement data yet. Students will appear here once they start training.</p>
              </div>
            )}
          </div>
        )}

        {/* ── Pastoral ──────────────────────────────────────────── */}
        {tab === 'pastoral' && (
          <div data-testid="pastoral-tab">
            <h1 className="text-xl font-bold mb-1" style={{ color: '#1A1209' }}>Pastoral Overview</h1>
            <p className="text-xs mb-5" style={{ color: '#999' }}>
              Aggregated mood indicators for your students. Individual conversations are never shown.
            </p>

            {/* Wellbeing Alerts Banner */}
            {(alertSummary.urgent > 0 || alertSummary.concern > 0) && (
              <div className="rounded-xl p-4 mb-5" style={{ backgroundColor: alertSummary.urgent > 0 ? '#FEF2F2' : '#FFFBEB', border: `1px solid ${alertSummary.urgent > 0 ? '#FECACA' : '#FEF3C7'}` }} data-testid="wellbeing-alerts-banner">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <AlertTriangle size={16} style={{ color: alertSummary.urgent > 0 ? '#DC2626' : '#F59E0B' }} />
                    <h3 className="text-sm font-bold" style={{ color: '#1A1209' }}>Wellbeing Alerts</h3>
                  </div>
                  <div className="flex gap-2">
                    {alertSummary.urgent > 0 && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ backgroundColor: '#DC2626', color: 'white' }}>{alertSummary.urgent} Urgent</span>}
                    {alertSummary.concern > 0 && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ backgroundColor: '#F59E0B', color: 'white' }}>{alertSummary.concern} Concern</span>}
                    {alertSummary.watch > 0 && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ backgroundColor: '#6B7280', color: 'white' }}>{alertSummary.watch} Watch</span>}
                  </div>
                </div>
                <div className="space-y-2">
                  {alerts.filter(a => !a.acknowledged).slice(0, 5).map(a => {
                    const sevColors = { urgent: { bg: '#FEE2E2', border: '#FECACA', color: '#991B1B' }, concern: { bg: '#FEF3C7', border: '#FDE68A', color: '#92400E' }, watch: { bg: '#F3F4F6', border: '#E5E7EB', color: '#4B5563' } };
                    const sc = sevColors[a.severity] || sevColors.watch;
                    return (
                      <div key={a.alert_id} className="flex items-center justify-between rounded-lg px-3 py-2.5" style={{ backgroundColor: sc.bg, border: `1px solid ${sc.border}` }} data-testid={`alert-${a.alert_id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="px-1.5 py-0.5 rounded text-[9px] font-black uppercase" style={{ backgroundColor: sc.color, color: 'white' }}>{a.severity}</span>
                            <span className="text-xs font-semibold truncate" style={{ color: '#1A1209' }}>{a.title}</span>
                          </div>
                          <p className="text-[10px]" style={{ color: sc.color }}>{a.description}</p>
                        </div>
                        <button
                          onClick={async () => {
                            await fetch(`${API_URL}/api/school/wellbeing/alerts/${a.alert_id}/acknowledge`, {
                              method: 'PUT', headers: { Authorization: `Bearer ${getToken()}` },
                            });
                            setAlerts(prev => prev.map(x => x.alert_id === a.alert_id ? { ...x, acknowledged: true } : x));
                            setAlertSummary(prev => ({ ...prev, [a.severity]: Math.max(0, prev[a.severity] - 1) }));
                          }}
                          className="ml-3 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold flex-shrink-0"
                          style={{ backgroundColor: 'white', color: sc.color, border: `1px solid ${sc.border}` }}
                          data-testid={`ack-alert-${a.alert_id}`}
                        >
                          <CheckCircle size={10} className="inline mr-1" /> Acknowledge
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Export pastoral data */}
            <div className="flex justify-end mb-3">
              <button
                onClick={() => {
                  const token = getToken();
                  fetch(`${API_URL}/api/school/export/pastoral`, { headers: { Authorization: `Bearer ${token}` } })
                    .then(r => r.blob()).then(b => { const u = URL.createObjectURL(b); const a = document.createElement('a'); a.href = u; a.download = 'pastoral_export.csv'; a.click(); URL.revokeObjectURL(u); });
                }}
                className="px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5"
                style={{ backgroundColor: '#F0F0F0', color: '#555' }} data-testid="export-pastoral-csv"
              >
                <Download size={12} /> Export Pastoral CSV
              </button>
            </div>

            {/* Privacy banner */}
            <div className="flex items-center gap-2 px-4 py-2.5 mb-5 rounded-lg text-xs" style={{ backgroundColor: '#EFF6FF', color: '#2563EB', border: '1px solid #BFDBFE' }}>
              <AlertTriangle size={14} />
              <span>This view shows <strong>mood categories only</strong>. No conversation content is accessible.</span>
            </div>

            {pastoral ? (
              <>
                {/* Mood Summary Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6" data-testid="pastoral-mood-summary">
                  {[
                    { key: 'thriving', label: 'Thriving', colour: '#2EAA5C', bg: '#F0FDF4' },
                    { key: 'steady', label: 'Steady', colour: '#2563EB', bg: '#EFF6FF' },
                    { key: 'watchful', label: 'Watchful', colour: '#F59E0B', bg: '#FFFBEB' },
                    { key: 'needs_attention', label: 'Needs Attention', colour: '#DC2626', bg: '#FEF2F2' },
                    { key: 'inactive', label: 'Inactive', colour: '#9CA3AF', bg: '#F9FAFB' },
                  ].map(cat => (
                    <div key={cat.key} className="rounded-xl p-4" style={{ backgroundColor: cat.bg, border: `1px solid ${cat.colour}25` }} data-testid={`pastoral-card-${cat.key}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.colour }} />
                        <span className="text-xs font-semibold" style={{ color: cat.colour }}>{cat.label}</span>
                      </div>
                      <p className="text-3xl font-bold" style={{ color: cat.colour }}>{pastoral.mood_summary?.[cat.key] || 0}</p>
                      <p className="text-[10px] mt-1" style={{ color: '#999' }}>students</p>
                    </div>
                  ))}
                </div>

                {/* Student Mood Table */}
                <div className="bg-white rounded-xl overflow-hidden" style={{ border: '1px solid #E8E8E8' }} data-testid="pastoral-student-table">
                  <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid #E8E8E8' }}>
                    <h3 className="text-sm font-bold" style={{ color: '#1A1209' }}>Student Wellbeing ({pastoral.period})</h3>
                    <span className="text-[10px]" style={{ color: '#999' }}>{pastoral.total_students} students</span>
                  </div>
                  <table className="w-full">
                    <thead>
                      <tr style={{ backgroundColor: '#F8F7F4', borderBottom: '1px solid #E8E8E8' }}>
                        <th className="px-5 py-2.5 text-left text-[11px] font-semibold uppercase" style={{ color: '#999' }}>Student</th>
                        <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase" style={{ color: '#999' }}>Grade</th>
                        <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase" style={{ color: '#999' }}>Teacher</th>
                        <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase" style={{ color: '#999' }}>Mood</th>
                        <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase" style={{ color: '#999' }}>Sessions</th>
                        <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase" style={{ color: '#999' }}>Last Active</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(pastoral.students || []).length === 0 ? (
                        <tr><td colSpan={6} className="px-5 py-12 text-center text-sm" style={{ color: '#AAA' }}>No students enrolled yet.</td></tr>
                      ) : (pastoral.students || []).map((s, i) => (
                        <tr key={s.student_id || i} className="hover:bg-gray-50" style={{ borderBottom: '1px solid #F0F0F0' }} data-testid={`pastoral-row-${i}`}>
                          <td className="px-5 py-3 text-sm font-medium" style={{ color: '#1A1209' }}>{s.first_name} {s.last_initial}</td>
                          <td className="px-4 py-3 text-xs">
                            <span className="px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: '#F5831F15', color: '#F5831F' }}>{s.grade || '-'}</span>
                          </td>
                          <td className="px-4 py-3 text-xs" style={{ color: '#666' }}>{s.teacher || '-'}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: s.colour }} />
                              <span className="text-xs font-medium capitalize" style={{ color: s.colour }}>
                                {s.category?.replace('_', ' ')}
                              </span>
                              {s.avg_mood !== null && (
                                <span className="text-[10px] font-mono" style={{ color: '#999' }}>({s.avg_mood})</span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-xs font-bold" style={{ color: '#333' }}>{s.sessions_30d}</td>
                          <td className="px-4 py-3 text-xs" style={{ color: '#999' }}>
                            {s.last_active ? new Date(s.last_active).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Never'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Class OCEAN Profile Aggregates */}
                {oceanAggregates && oceanAggregates.students_with_profile > 0 && (
                  <div className="bg-white rounded-xl overflow-hidden mt-6" style={{ border: '1px solid #E8E8E8' }} data-testid="pastoral-ocean-aggregates">
                    <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid #E8E8E8' }}>
                      <h3 className="text-sm font-bold" style={{ color: '#1A1209' }}>Class Social Profile</h3>
                      <span className="text-[10px]" style={{ color: '#999' }}>{oceanAggregates.students_with_profile} of {oceanAggregates.total_students} students profiled</span>
                    </div>
                    <div className="p-5">
                      <p className="text-xs mb-4" style={{ color: '#888' }}>Aggregated class averages. Individual student profiles are never shown.</p>
                      {[
                        { key: 'openness', label: 'Curiosity', color: '#7B4FD8' },
                        { key: 'conscientiousness', label: 'Follow-through', color: '#2563EB' },
                        { key: 'extraversion', label: 'Social Energy', color: '#F5831F' },
                        { key: 'agreeableness', label: 'Empathy', color: '#2EAA5C' },
                        { key: 'neuroticism', label: 'Emotional Sensitivity', color: '#EC4899' },
                      ].map((trait) => {
                        const avg = oceanAggregates.averages?.[trait.key] || 0;
                        const dist = oceanAggregates.distribution?.[trait.key] || {};
                        const pct = Math.max(0, Math.min(100, (avg / 5) * 100));
                        return (
                          <div key={trait.key} className="mb-4" data-testid={`pastoral-ocean-${trait.key}`}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-semibold" style={{ color: '#1A1209' }}>{trait.label}</span>
                              <span className="text-[10px]" style={{ color: '#999' }}>avg {avg}/5</span>
                            </div>
                            {/* Average bar */}
                            <div className="w-full h-2.5 rounded-full mb-1" style={{ backgroundColor: '#F0F0F0' }}>
                              <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, backgroundColor: trait.color }} />
                            </div>
                            {/* Distribution stacked bar */}
                            <div className="relative group">
                              <div className="flex h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: '#F0F0F0' }}>
                                {dist.low > 0 && <div style={{ width: `${dist.low}%`, backgroundColor: '#94A3B8' }} title={`Low: ${dist.low}%`} />}
                                {dist.medium > 0 && <div style={{ width: `${dist.medium}%`, backgroundColor: trait.color + '60' }} title={`Medium: ${dist.medium}%`} />}
                                {dist.high > 0 && <div style={{ width: `${dist.high}%`, backgroundColor: trait.color }} title={`High: ${dist.high}%`} />}
                              </div>
                              <div className="hidden group-hover:flex gap-3 mt-1 text-[9px]" style={{ color: '#999' }}>
                                <span>Low (1-2): {dist.low || 0}%</span>
                                <span>Medium (3): {dist.medium || 0}%</span>
                                <span>High (4-5): {dist.high || 0}%</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="bg-white rounded-xl p-8 text-center" style={{ border: '1px solid #E8E8E8' }}>
                <Heart size={32} className="mx-auto mb-3" style={{ color: '#DDD' }} />
                <p className="text-sm" style={{ color: '#999' }}>Pastoral data is available once students are enrolled and active.</p>
              </div>
            )}
          </div>
        )}

        {/* ── Certificates ──────────────────────────────────────── */}
        {tab === 'certificates' && (
          <div data-testid="certificates-tab">
            <h1 className="text-xl font-bold mb-1" style={{ color: '#1A1209' }}>Student Certificates</h1>
            <p className="text-xs mb-5" style={{ color: '#999' }}>
              Recognise student achievements with printable certificates. Click a student to view and print their awards.
            </p>

            {/* Summary */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
              <StatCard label="Total Certificates" value={certificates?.total_certificates || 0} icon={<Award size={16} style={{ color: '#F5831F' }} />} />
              <StatCard label="Students Awarded" value={certificates?.students?.length || 0} icon={<Users size={16} style={{ color: '#2563EB' }} />} />
              <StatCard label="Certificate Types" value={certificates?.templates?.length || 0} icon={<Trophy size={16} style={{ color: '#10B981' }} />} />
            </div>

            {certificates?.students?.length > 0 ? (
              <div className="space-y-3">
                {certificates.students.map(s => (
                  <div key={s.student_id} className="bg-white rounded-xl p-4" style={{ border: '1px solid #E8E8E8' }} data-testid={`cert-student-${s.student_id}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ backgroundColor: '#F5831F' }}>
                          {s.student_name?.[0]}
                        </div>
                        <div>
                          <h4 className="text-sm font-bold" style={{ color: '#1A1209' }}>{s.student_name}</h4>
                          <p className="text-[10px]" style={{ color: '#999' }}>{s.grade} &middot; {s.teacher}</p>
                        </div>
                      </div>
                      <span className="px-2.5 py-1 rounded-full text-[10px] font-bold" style={{ backgroundColor: '#F5831F15', color: '#F5831F' }}>
                        {s.total_earned} award{s.total_earned !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {s.certificates.map(c => {
                        const iconMap = { 'message-circle': Activity, trophy: Trophy, zap: Zap, award: Award, star: Trophy, heart: Heart };
                        const CIcon = iconMap[c.icon] || Award;
                        return (
                          <div key={c.cert_id} className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ backgroundColor: '#FFFBEB', border: '1px solid #FDE68A' }}>
                            <CIcon size={14} style={{ color: '#F59E0B' }} />
                            <div>
                              <p className="text-[10px] font-bold" style={{ color: '#92400E' }}>{c.title}</p>
                              <p className="text-[9px]" style={{ color: '#B45309' }}>{c.description}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <button
                      onClick={() => {
                        const token = getToken();
                        fetch(`${API_URL}/api/school/certificates/${s.student_id}`, { headers: { Authorization: `Bearer ${token}` } })
                          .then(r => r.json()).then(data => {
                            const w = window.open('', '_blank');
                            w.document.write(`<html><head><title>Certificate - ${data.student_name}</title><style>body{font-family:Georgia,serif;text-align:center;padding:60px;background:#FFFBEB}h1{color:#1A1209;font-size:36px;margin:0 0 8px}h2{color:#F5831F;font-size:20px;margin:0 0 20px;font-weight:normal}p{color:#666;font-size:14px;margin:4px 0}.cert{border:3px solid #F5831F;border-radius:16px;padding:40px;max-width:700px;margin:0 auto;background:white}.awards{display:flex;flex-wrap:wrap;gap:12px;justify-content:center;margin:20px 0}.award{background:#FFFBEB;border:1px solid #FDE68A;border-radius:8px;padding:8px 16px;font-size:13px;color:#92400E;font-weight:600}.school{color:#999;font-size:12px;margin-top:24px}@media print{body{background:white}}</style></head><body><div class="cert"><p style="color:#F5831F;font-size:12px;letter-spacing:4px;text-transform:uppercase">Certificate of Achievement</p><h1>${data.student_name}</h1><h2>${data.grade} — ${data.school_name}</h2><div class="awards">${data.certificates.map(c=>'<div class="award">'+c.title+'</div>').join('')}</div><p>Sessions: ${data.stats.sessions} | Challenges: ${data.stats.challenges} | Best Streak: ${data.stats.streak} days</p><p class="school">Presented by HiBFF & ${data.school_name}</p><p style="color:#CCC;font-size:11px;margin-top:16px">${new Date(data.generated_at).toLocaleDateString('en-US',{month:'long',day:'numeric',year:'numeric'})}</p></div><script>setTimeout(()=>window.print(),500)</script></body></html>`);
                          });
                      }}
                      className="mt-3 px-3 py-1.5 rounded-lg text-[10px] font-semibold flex items-center gap-1"
                      style={{ backgroundColor: '#F0F0F0', color: '#555' }} data-testid={`print-cert-${s.student_id}`}
                    >
                      <FileText size={10} /> Print Certificates
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-xl p-8 text-center" style={{ border: '1px solid #E8E8E8' }}>
                <Award size={32} style={{ color: '#DDD' }} className="mx-auto mb-3" />
                <p className="text-sm" style={{ color: '#999' }}>No certificates earned yet. Students earn certificates by completing sessions, challenges, and maintaining streaks.</p>
              </div>
            )}
          </div>
        )}

        {/* ── Teachers & Staff ──────────────────────────────────── */}
        {tab === 'staff' && (
          <div data-testid="staff-tab">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-xl font-bold" style={{ color: '#1A1209' }}>Teachers & Staff</h1>
                <p className="text-xs" style={{ color: '#999' }}>{staff.length} staff member{staff.length !== 1 ? 's' : ''}</p>
              </div>
              <button onClick={() => setShowInviteStaff(!showInviteStaff)}
                className="px-3 py-2 rounded-lg text-xs font-medium text-white flex items-center gap-1.5"
                style={{ backgroundColor: '#F5831F' }} data-testid="invite-staff-btn">
                <UserPlus size={13} /> Invite Staff
              </button>
            </div>

            {/* Invite form */}
            {showInviteStaff && (
              <div className="bg-white p-4 rounded-xl mb-4" style={{ border: '2px solid #F5831F33' }} data-testid="invite-staff-form">
                <h3 className="text-sm font-bold mb-3" style={{ color: '#1A1209' }}>Invite New Staff Member</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                  <input value={staffForm.name} onChange={e => setStaffForm(p => ({...p, name: e.target.value}))}
                    placeholder="Full name *" className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="staff-name" />
                  <input value={staffForm.email} onChange={e => setStaffForm(p => ({...p, email: e.target.value}))}
                    placeholder="Email *" type="email" className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="staff-email" />
                  <select value={staffForm.role} onChange={e => setStaffForm(p => ({...p, role: e.target.value}))}
                    className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="staff-role">
                    <option value="teacher">Teacher</option>
                    <option value="counselor">Counselor</option>
                    <option value="admin">Admin</option>
                  </select>
                  <select multiple value={staffForm.classes} onChange={e => setStaffForm(p => ({...p, classes: [...e.target.selectedOptions].map(o => o.value)}))}
                    className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="staff-classes">
                    {availableClasses.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="flex gap-2">
                  <button onClick={async () => {
                    if (!staffForm.name || !staffForm.email) return;
                    setInvitingStaff(true);
                    try {
                      const res = await fetch(`${API_URL}/api/school/staff/invite`, {
                        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
                        body: JSON.stringify(staffForm),
                      });
                      if (res.ok) {
                        setStaffForm({ email: '', name: '', role: 'teacher', classes: [] });
                        setShowInviteStaff(false);
                        fetchData();
                      }
                    } catch (err) { /* handled */}
                    setInvitingStaff(false);
                  }} disabled={invitingStaff || !staffForm.name || !staffForm.email}
                    className="px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50 flex items-center gap-1.5"
                    style={{ backgroundColor: '#2EAA5C' }} data-testid="save-staff-btn">
                    {invitingStaff ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Send Invite
                  </button>
                  <button onClick={() => setShowInviteStaff(false)} className="px-4 py-2 rounded-lg text-sm" style={{ backgroundColor: '#F0F0F0', color: '#666' }}>Cancel</button>
                </div>
              </div>
            )}

            {/* Role legend */}
            <div className="flex gap-3 mb-4">
              {[
                { role: 'admin', label: 'Admin', color: '#DC2626', desc: 'Full dashboard access' },
                { role: 'counselor', label: 'Counselor', color: '#8B5CF6', desc: 'Pastoral + alerts only' },
                { role: 'teacher', label: 'Teacher', color: '#2563EB', desc: 'Own class data only' },
              ].map(r => (
                <div key={r.role} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg" style={{ backgroundColor: `${r.color}08` }}>
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: r.color }} />
                  <span className="text-[10px] font-semibold" style={{ color: r.color }}>{r.label}</span>
                  <span className="text-[9px]" style={{ color: '#999' }}>— {r.desc}</span>
                </div>
              ))}
            </div>

            {/* Staff list */}
            {staff.length > 0 ? (
              <div className="bg-white rounded-xl overflow-hidden" style={{ border: '1px solid #E8E8E8' }}>
                <table className="w-full">
                  <thead>
                    <tr style={{ backgroundColor: '#F8F7F4', borderBottom: '1px solid #E8E8E8' }}>
                      {['Name', 'Email', 'Role', 'Classes', 'Status', 'Actions'].map(h => (
                        <th key={h} className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase" style={{ color: '#999' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {staff.map(s => {
                      const roleColors = { admin: '#DC2626', counselor: '#8B5CF6', teacher: '#2563EB' };
                      const rc = roleColors[s.role] || '#999';
                      return (
                        <tr key={s.staff_id} className="hover:bg-gray-50" style={{ borderBottom: '1px solid #F0F0F0' }} data-testid={`staff-row-${s.staff_id}`}>
                          <td className="px-4 py-3 text-sm font-medium" style={{ color: '#1A1209' }}>{s.name}</td>
                          <td className="px-4 py-3 text-xs" style={{ color: '#666' }}>{s.email}</td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold capitalize" style={{ backgroundColor: `${rc}15`, color: rc }}>{s.role}</span>
                          </td>
                          <td className="px-4 py-3 text-xs" style={{ color: '#666' }}>{s.classes?.join(', ') || '—'}</td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ backgroundColor: s.status === 'active' ? '#ECFDF5' : '#FEF3C7', color: s.status === 'active' ? '#059669' : '#92400E' }}>
                              {s.status === 'active' ? 'Active' : 'Invited'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button onClick={async () => {
                              if (!window.confirm(`Remove ${s.name} from staff?`)) return;
                              await fetch(`${API_URL}/api/school/staff/${s.staff_id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${getToken()}` } });
                              fetchData();
                            }} className="p-1.5 rounded hover:bg-gray-100" data-testid={`remove-staff-${s.staff_id}`}>
                              <Trash2 size={13} style={{ color: '#DC2626' }} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="bg-white rounded-xl p-8 text-center" style={{ border: '1px solid #E8E8E8' }}>
                <UserCheck size={32} style={{ color: '#DDD' }} className="mx-auto mb-3" />
                <p className="text-sm" style={{ color: '#999' }}>No staff members yet. Invite teachers and counselors to give them view-only access to relevant student data.</p>
              </div>
            )}
          </div>
        )}

        {/* ── Students ────────────────────────────────────────────── */}
        {tab === 'students' && (
          <div data-testid="students-tab">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-xl font-bold" style={{ color: '#1A1209' }}>Student Roster</h1>
                <p className="text-xs" style={{ color: '#999' }}>{studentTotal} student{studentTotal !== 1 ? 's' : ''} enrolled</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => { setShowBulkUpload(!showBulkUpload); setShowAddForm(false); }}
                  className="px-3 py-2 rounded-lg text-xs font-medium flex items-center gap-1.5"
                  style={{ backgroundColor: '#F0F0F0', color: '#555' }} data-testid="bulk-upload-btn">
                  <Upload size={13} /> Bulk CSV Upload
                </button>
                <button onClick={() => { setShowAddForm(!showAddForm); setShowBulkUpload(false); }}
                  className="px-3 py-2 rounded-lg text-xs font-medium text-white flex items-center gap-1.5"
                  style={{ backgroundColor: '#F5831F' }} data-testid="add-student-btn">
                  <Plus size={13} /> Add Student
                </button>
              </div>
            </div>

            {/* Bulk upload panel */}
            {showBulkUpload && (
              <div className="bg-white p-4 rounded-xl mb-4" style={{ border: '1px solid #E8E8E8' }} data-testid="bulk-upload-panel">
                <h3 className="text-sm font-bold mb-2" style={{ color: '#1A1209' }}>Upload Students via CSV</h3>
                <p className="text-xs mb-3" style={{ color: '#888' }}>
                  CSV columns: <code className="px-1 py-0.5 rounded text-[10px]" style={{ backgroundColor: '#F0F0F0' }}>first_name, last_name, grade, section, teacher_name, gender, parent_email, parent_name</code>
                </p>
                <input type="file" ref={fileRef} accept=".csv" onChange={handleBulkUpload} className="hidden" />
                <button onClick={() => fileRef.current?.click()} disabled={uploading}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50 flex items-center gap-1.5"
                  style={{ backgroundColor: '#2563EB' }} data-testid="csv-upload-trigger">
                  {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                  {uploading ? 'Uploading...' : 'Select CSV File'}
                </button>
                {uploadResult && (
                  <div className="mt-3 text-xs p-3 rounded-lg" style={{ backgroundColor: uploadResult.added > 0 ? '#F0FDF4' : '#FEF2F2' }}>
                    <p><strong>{uploadResult.added}</strong> students added{uploadResult.skipped > 0 && <>, <strong>{uploadResult.skipped}</strong> skipped</>}</p>
                    {uploadResult.errors?.length > 0 && uploadResult.errors.map((e, i) => <p key={i} style={{ color: '#DC2626' }}>{e}</p>)}
                  </div>
                )}
              </div>
            )}

            {/* Add student form */}
            {showAddForm && (
              <div className="bg-white p-4 rounded-xl mb-4" style={{ border: '2px solid #F5831F33' }} data-testid="add-student-form">
                <h3 className="text-sm font-bold mb-3" style={{ color: '#1A1209' }}>Add Student</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
                  <input value={addForm.first_name} onChange={e => setAddForm(p => ({...p, first_name: e.target.value}))}
                    placeholder="First name *" className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="add-first-name" />
                  <input value={addForm.last_name} onChange={e => setAddForm(p => ({...p, last_name: e.target.value}))}
                    placeholder="Last name *" className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="add-last-name" />
                  <select value={addForm.grade} onChange={e => setAddForm(p => ({...p, grade: e.target.value}))}
                    className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="add-grade">
                    <option value="">Grade</option>
                    {US_GRADES.map(g => <option key={g} value={g}>{g}</option>)}
                  </select>
                  <input value={addForm.section} onChange={e => setAddForm(p => ({...p, section: e.target.value}))}
                    placeholder="Section / Class" className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="add-section" />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                  <input value={addForm.teacher_name} onChange={e => setAddForm(p => ({...p, teacher_name: e.target.value}))}
                    placeholder="Teacher name" className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="add-teacher" />
                  <select value={addForm.gender} onChange={e => setAddForm(p => ({...p, gender: e.target.value}))}
                    className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="add-gender">
                    <option value="">Gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                  <input value={addForm.parent_email} onChange={e => setAddForm(p => ({...p, parent_email: e.target.value}))}
                    placeholder="Parent email" className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="add-parent-email" />
                  <input value={addForm.parent_name} onChange={e => setAddForm(p => ({...p, parent_name: e.target.value}))}
                    placeholder="Parent name" className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="add-parent-name" />
                </div>
                <div className="flex gap-2">
                  <button onClick={addStudent} disabled={saving || !addForm.first_name.trim() || !addForm.last_name.trim()}
                    className="px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50 flex items-center gap-1.5"
                    style={{ backgroundColor: '#2EAA5C' }} data-testid="save-student-btn">
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save
                  </button>
                  <button onClick={() => setShowAddForm(false)} className="px-4 py-2 rounded-lg text-sm font-medium" style={{ backgroundColor: '#F0F0F0', color: '#666' }}>Cancel</button>
                </div>
              </div>
            )}

            {/* Filters */}
            <div className="flex gap-2 mb-4">
              <div className="relative flex-1">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#AAA' }} />
                <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search students..."
                  className="w-full pl-8 pr-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="student-search" />
              </div>
              <select value={gradeFilter} onChange={e => setGradeFilter(e.target.value)}
                className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="grade-filter">
                <option value="">All Grades</option>
                {US_GRADES.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
              <select value={teacherFilter} onChange={e => setTeacherFilter(e.target.value)}
                className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="teacher-filter">
                <option value="">All Teachers</option>
                {summary?.by_teacher?.map(t => <option key={t.teacher} value={t.teacher}>{t.teacher} ({t.count})</option>)}
              </select>
            </div>

            {/* Bulk Actions */}
            {selectedStudents.length > 0 && (
              <div className="flex items-center gap-3 mb-3 px-3 py-2.5 rounded-lg" style={{ backgroundColor: '#EFF6FF', border: '1px solid #BFDBFE' }} data-testid="bulk-actions-bar">
                <span className="text-xs font-semibold" style={{ color: '#2563EB' }}>{selectedStudents.length} selected</span>
                <button onClick={async () => {
                  try {
                    const res = await fetch(`${API_URL}/api/school/students/bulk-nudge`, {
                      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
                      body: JSON.stringify({ student_ids: selectedStudents }),
                    });
                    if (res.ok) { const d = await res.json(); alert(`Nudged ${d.nudged_count} students`); setSelectedStudents([]); }
                  } catch (err) { /* handled */}
                }} className="px-3 py-1.5 rounded-lg text-[10px] font-semibold text-white" style={{ backgroundColor: '#2563EB' }} data-testid="bulk-nudge-btn">
                  <Bell size={10} className="inline mr-1" /> Nudge Selected
                </button>
                <button onClick={() => setBulkAction('assign')} className="px-3 py-1.5 rounded-lg text-[10px] font-semibold" style={{ backgroundColor: '#F0F0F0', color: '#555' }} data-testid="bulk-assign-btn">
                  <Users size={10} className="inline mr-1" /> Assign Class/Teacher
                </button>
                <button onClick={() => setSelectedStudents([])} className="text-[10px] ml-auto" style={{ color: '#999' }}>Clear</button>
              </div>
            )}

            {/* Bulk assign modal */}
            {bulkAction === 'assign' && (
              <div className="bg-white p-4 rounded-xl mb-3" style={{ border: '2px solid #2563EB33' }}>
                <h3 className="text-sm font-bold mb-3" style={{ color: '#1A1209' }}>Bulk Assign {selectedStudents.length} Students</h3>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <input placeholder="Section / Class" id="bulk-section" className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} />
                  <input placeholder="Teacher Name" id="bulk-teacher" className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} />
                </div>
                <div className="flex gap-2">
                  <button onClick={async () => {
                    const section = document.getElementById('bulk-section').value;
                    const teacher = document.getElementById('bulk-teacher').value;
                    if (!section && !teacher) return;
                    try {
                      const res = await fetch(`${API_URL}/api/school/students/bulk-assign`, {
                        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
                        body: JSON.stringify({ student_ids: selectedStudents, section, teacher_name: teacher }),
                      });
                      if (res.ok) { const d = await res.json(); alert(`Updated ${d.updated_count} students`); setBulkAction(null); setSelectedStudents([]); fetchStudents(); }
                    } catch (err) { /* handled */}
                  }} className="px-4 py-2 rounded-lg text-sm font-medium text-white" style={{ backgroundColor: '#2EAA5C' }}>Apply</button>
                  <button onClick={() => setBulkAction(null)} className="px-4 py-2 rounded-lg text-sm" style={{ backgroundColor: '#F0F0F0', color: '#666' }}>Cancel</button>
                </div>
              </div>
            )}

            {/* Student table */}
            <div className="bg-white rounded-xl overflow-hidden" style={{ border: '1px solid #E8E8E8' }} data-testid="student-table">
              <table className="w-full">
                <thead>
                  <tr style={{ backgroundColor: '#F8F7F4', borderBottom: '1px solid #E8E8E8' }}>
                    <th className="px-2 py-2.5 w-8">
                      <input type="checkbox" checked={selectedStudents.length === students.length && students.length > 0}
                        onChange={e => setSelectedStudents(e.target.checked ? students.map(s => s.student_id) : [])}
                        className="w-3.5 h-3.5 rounded" data-testid="select-all-students" />
                    </th>
                    <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase" style={{ color: '#999' }}>Name</th>
                    <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase" style={{ color: '#999' }}>Grade</th>
                    <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase" style={{ color: '#999' }}>Section</th>
                    <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase" style={{ color: '#999' }}>Teacher</th>
                    <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase" style={{ color: '#999' }}>Gender</th>
                    <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase" style={{ color: '#999' }}>Parent</th>
                    <th className="px-4 py-2.5 text-right text-[11px] font-semibold uppercase" style={{ color: '#999' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {students.length === 0 ? (
                    <tr><td colSpan={8} className="px-4 py-12 text-center text-sm" style={{ color: '#AAA' }}>No students found. Add students individually or upload a CSV.</td></tr>
                  ) : students.map((s, i) => {
                    const isEditing = editingStudent === s.student_id;
                    const isSelected = selectedStudents.includes(s.student_id);
                    return (
                      <tr key={s.student_id} className="hover:bg-gray-50" style={{ borderBottom: '1px solid #F0F0F0', backgroundColor: isSelected ? '#EFF6FF' : i % 2 === 0 ? undefined : undefined }} data-testid={`student-row-${i}`}>
                        <td className="px-2 py-2.5 w-8">
                          <input type="checkbox" checked={isSelected}
                            onChange={e => setSelectedStudents(prev => e.target.checked ? [...prev, s.student_id] : prev.filter(id => id !== s.student_id))}
                            className="w-3.5 h-3.5 rounded" />
                        </td>
                        {isEditing ? (
                          <>
                            <td className="px-4 py-2">
                              <div className="flex gap-1">
                                <input value={editForm.first_name} onChange={e => setEditForm(p => ({...p, first_name: e.target.value}))} className="px-1.5 py-1 border rounded text-xs w-20" style={{ borderColor: '#E0E0E0' }} />
                                <input value={editForm.last_name} onChange={e => setEditForm(p => ({...p, last_name: e.target.value}))} className="px-1.5 py-1 border rounded text-xs w-20" style={{ borderColor: '#E0E0E0' }} />
                              </div>
                            </td>
                            <td className="px-4 py-2">
                              <select value={editForm.grade} onChange={e => setEditForm(p => ({...p, grade: e.target.value}))} className="px-1.5 py-1 border rounded text-xs" style={{ borderColor: '#E0E0E0' }}>
                                <option value="">-</option>
                                {US_GRADES.map(g => <option key={g} value={g}>{g}</option>)}
                              </select>
                            </td>
                            <td className="px-4 py-2"><input value={editForm.section} onChange={e => setEditForm(p => ({...p, section: e.target.value}))} className="px-1.5 py-1 border rounded text-xs w-16" style={{ borderColor: '#E0E0E0' }} /></td>
                            <td className="px-4 py-2"><input value={editForm.teacher_name} onChange={e => setEditForm(p => ({...p, teacher_name: e.target.value}))} className="px-1.5 py-1 border rounded text-xs w-24" style={{ borderColor: '#E0E0E0' }} /></td>
                            <td className="px-4 py-2">
                              <select value={editForm.gender} onChange={e => setEditForm(p => ({...p, gender: e.target.value}))} className="px-1.5 py-1 border rounded text-xs" style={{ borderColor: '#E0E0E0' }}>
                                <option value="">-</option><option value="male">M</option><option value="female">F</option><option value="other">O</option>
                              </select>
                            </td>
                            <td className="px-4 py-2"><input value={editForm.parent_email} onChange={e => setEditForm(p => ({...p, parent_email: e.target.value}))} className="px-1.5 py-1 border rounded text-xs w-28" style={{ borderColor: '#E0E0E0' }} /></td>
                            <td className="px-4 py-2 text-right">
                              <div className="flex gap-1 justify-end">
                                <button onClick={() => saveEdit(s.student_id)} className="p-1.5 rounded" style={{ backgroundColor: '#2EAA5C15' }}><Check size={13} style={{ color: '#2EAA5C' }} /></button>
                                <button onClick={() => setEditingStudent(null)} className="p-1.5 rounded" style={{ backgroundColor: '#F0F0F0' }}><X size={13} style={{ color: '#999' }} /></button>
                              </div>
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="px-4 py-2.5 text-sm font-medium" style={{ color: '#1A1209' }}>{s.first_name} {s.last_name}</td>
                            <td className="px-4 py-2.5 text-xs">
                              <span className="px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: '#F5831F15', color: '#F5831F' }}>{s.grade || '-'}</span>
                            </td>
                            <td className="px-4 py-2.5 text-xs" style={{ color: '#666' }}>{s.section || '-'}</td>
                            <td className="px-4 py-2.5 text-xs" style={{ color: '#666' }}>{s.teacher_name || '-'}</td>
                            <td className="px-4 py-2.5 text-xs capitalize" style={{ color: '#666' }}>{s.gender || '-'}</td>
                            <td className="px-4 py-2.5 text-xs" style={{ color: '#666' }}>{s.parent_email || '-'}</td>
                            <td className="px-4 py-2.5 text-right">
                              <div className="flex gap-1 justify-end">
                                <button onClick={() => startEdit(s)} className="p-1.5 rounded hover:bg-gray-100" data-testid={`edit-student-${i}`}><Edit2 size={13} style={{ color: '#F5831F' }} /></button>
                                <button onClick={() => deleteStudent(s.student_id, `${s.first_name} ${s.last_name}`)} className="p-1.5 rounded hover:bg-gray-100" data-testid={`delete-student-${i}`}><Trash2 size={13} style={{ color: '#DC2626' }} /></button>
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Access Codes ────────────────────────────────────────── */}
        {tab === 'access-codes' && (
          <div data-testid="access-codes-tab">
            <h1 className="text-xl font-bold mb-1" style={{ color: '#1A1209' }}>Parent Enrollment Codes</h1>
            <p className="text-xs mb-5" style={{ color: '#999' }}>
              Share this code with parents so they can self-register and activate their child&apos;s HiBFF account.
            </p>

            {/* Active code card */}
            {activeCode ? (
              <div className="bg-white rounded-xl p-6 mb-6" style={{ border: '1px solid #E8E8E8' }} data-testid="active-code-card">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-bold" style={{ color: '#1A1209' }}>Active Code</h3>
                  {new Date(activeCode.expires_at) < new Date() ? (
                    <span className="px-2.5 py-1 rounded-full text-[10px] font-semibold" style={{ backgroundColor: '#FEF2F2', color: '#DC2626' }} data-testid="code-expired-badge">Expired — regenerate below</span>
                  ) : (
                    <span className="px-2.5 py-1 rounded-full text-[10px] font-semibold" style={{ backgroundColor: '#2EAA5C15', color: '#2EAA5C' }}>Active</span>
                  )}
                </div>

                <div className="flex items-center gap-4 mb-4">
                  <div className="flex-1 rounded-xl p-4 text-center" style={{ backgroundColor: '#F8F7F4' }}>
                    <p className="text-3xl font-bold tracking-[6px]" style={{ color: '#1A1209', fontFamily: 'monospace' }} data-testid="access-code-value">{activeCode.code}</p>
                  </div>
                  <button onClick={() => copyCode(activeCode.code)}
                    className="px-4 py-3 rounded-lg text-sm font-medium flex items-center gap-1.5"
                    style={{ backgroundColor: codeCopied ? '#2EAA5C' : '#F5831F', color: 'white' }}
                    data-testid="copy-code-btn">
                    {codeCopied ? <Check size={14} /> : <Copy size={14} />}
                    {codeCopied ? 'Copied' : 'Copy'}
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                  <div className="rounded-lg p-3 text-center" style={{ backgroundColor: '#F8F7F4' }}>
                    <p className="text-[10px] font-medium mb-1" style={{ color: '#999' }}>Created</p>
                    <p className="text-xs font-semibold" style={{ color: '#333' }}>{new Date(activeCode.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                  </div>
                  <div className="rounded-lg p-3 text-center" style={{ backgroundColor: '#F8F7F4' }}>
                    <p className="text-[10px] font-medium mb-1" style={{ color: '#999' }}>Expires</p>
                    <p className="text-xs font-semibold" style={{ color: '#333' }}>{new Date(activeCode.expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                  </div>
                  <div className="rounded-lg p-3 text-center" style={{ backgroundColor: '#F8F7F4' }}>
                    <p className="text-[10px] font-medium mb-1" style={{ color: '#999' }}>Parents Enrolled</p>
                    <p className="text-xs font-semibold" style={{ color: '#F5831F' }} data-testid="usage-count">{activeCode.usage_count || 0}</p>
                  </div>
                </div>

                <button onClick={regenerateCode} disabled={regenerating}
                  className="px-4 py-2 rounded-lg text-xs font-medium flex items-center gap-1.5 disabled:opacity-50"
                  style={{ backgroundColor: '#FEF2F2', color: '#DC2626' }}
                  data-testid="regenerate-code-btn">
                  {regenerating ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                  {regenerating ? 'Regenerating...' : 'Regenerate Code (deactivates current)'}
                </button>
              </div>
            ) : (
              <div className="bg-white rounded-xl p-6 mb-6 text-center" style={{ border: '1px solid #E8E8E8' }}>
                <Key size={32} className="mx-auto mb-3" style={{ color: '#DDD' }} />
                <p className="text-sm mb-3" style={{ color: '#999' }}>No active enrollment code yet.</p>
                <button onClick={regenerateCode} disabled={regenerating}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-white flex items-center gap-1.5 mx-auto disabled:opacity-50"
                  style={{ backgroundColor: '#F5831F' }}
                  data-testid="generate-first-code-btn">
                  {regenerating ? <Loader2 size={14} className="animate-spin" /> : <Key size={14} />}
                  Generate Enrollment Code
                </button>
              </div>
            )}

            {/* How it works */}
            <div className="bg-white rounded-xl p-5" style={{ border: '1px solid #E8E8E8' }}>
              <h3 className="text-sm font-bold mb-3" style={{ color: '#1A1209' }}>How Enrollment Codes Work</h3>
              <div className="space-y-3">
                <InfoStep num="1" text="Share your school's enrollment code with parents via email, letter, or your school portal." />
                <InfoStep num="2" text="Parents enter the code during HiBFF registration to link their child to your school." />
                <InfoStep num="3" text="Parents activate their child's account — students never register directly (COPPA compliant)." />
                <InfoStep num="4" text="You see aggregated engagement data on your dashboard. Individual conversations remain private." />
              </div>
            </div>

            {/* Previous codes */}
            {accessCodes.filter(c => !c.active).length > 0 && (
              <div className="mt-6">
                <h3 className="text-sm font-bold mb-3" style={{ color: '#1A1209' }}>Previous Codes</h3>
                <div className="space-y-2">
                  {accessCodes.filter(c => !c.active).map(c => (
                    <div key={c.code} className="bg-white rounded-lg px-4 py-3 flex items-center justify-between" style={{ border: '1px solid #F0F0F0' }}>
                      <span className="text-sm font-mono" style={{ color: '#AAA' }}>{c.code}</span>
                      <div className="flex items-center gap-4">
                        <span className="text-xs" style={{ color: '#CCC' }}>{c.usage_count || 0} uses</span>
                        <span className="px-2 py-0.5 rounded-full text-[10px]" style={{ backgroundColor: '#F0F0F0', color: '#999' }}>Deactivated</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Reports ─────────────────────────────────────────── */}
        {tab === 'reports' && (
          <div data-testid="reports-tab">
            <h1 className="text-xl font-bold mb-1" style={{ color: '#1A1209' }}>Monthly Reports</h1>
            <p className="text-xs mb-5" style={{ color: '#999' }}>
              Aggregated monthly reports for your school. Individual student data is never included.
            </p>

            {/* Preview button — visible when school has at least 1 student */}
            {(summary?.total || 0) > 0 && (
              <div className="bg-white rounded-xl p-4 mb-5 flex items-center justify-between" style={{ border: '1px solid #E8E8E8' }} data-testid="preview-report-section">
                <div>
                  <p className="text-sm font-semibold" style={{ color: '#1A1209' }}>Preview This Month&apos;s Report</p>
                  <p className="text-[10px]" style={{ color: '#999' }}>Generates a watermarked PDF using real data from this month to date.</p>
                </div>
                <button onClick={async () => {
                  setPreviewing(true);
                  try {
                    const token = getToken();
                    const res = await fetch(`${API_URL}/api/reports/preview/school`, {
                      method: 'POST', headers: { Authorization: `Bearer ${token}` },
                    });
                    if (!res.ok) { const d = await res.json().catch(() => ({})); alert(d.detail || 'Preview generation failed'); return; }
                    const blob = await res.blob();
                    const url = URL.createObjectURL(blob);
                    const cd = res.headers.get('content-disposition') || '';
                    const fnMatch = cd.match(/filename="?([^"]+)"?/);
                    const fn = fnMatch ? fnMatch[1] : `HiBFF_Preview_Report.pdf`;
                    const a = document.createElement('a'); a.href = url; a.download = fn; a.click();
                    URL.revokeObjectURL(url);
                  } catch (err) { /* handled */ alert('Failed to generate preview'); }
                  setPreviewing(false);
                }}
                disabled={previewing}
                className="px-4 py-2 rounded-lg text-xs font-semibold text-white flex items-center gap-1.5 disabled:opacity-50"
                style={{ backgroundColor: '#7B4FD8' }}
                data-testid="preview-report-btn">
                  {previewing ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
                  {previewing ? 'Generating...' : "Preview This Month's Report"}
                </button>
              </div>
            )}

            {reports.length === 0 ? (
              <div className="bg-white rounded-xl p-8 text-center" style={{ border: '1px solid #E8E8E8' }}>
                <FileText size={32} className="mx-auto mb-3" style={{ color: '#DDD' }} />
                <p className="text-sm mb-1" style={{ color: '#999' }}>No reports generated yet</p>
                <p className="text-xs" style={{ color: '#CCC' }}>Reports are auto-generated on the 1st of each month after your school has been active for at least 14 days.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {reports.map(r => (
                  <div key={r.report_id} className="bg-white rounded-xl p-5 flex items-center justify-between" style={{ border: '1px solid #E8E8E8' }} data-testid={`report-${r.report_id}`}>
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#F5831F15' }}>
                        <FileText size={18} style={{ color: '#F5831F' }} />
                      </div>
                      <div>
                        <p className="text-sm font-bold" style={{ color: '#1A1209' }}>{r.month_name}</p>
                        <p className="text-xs" style={{ color: '#999' }}>
                          {r.active_students} active students &middot; {r.total_sessions} sessions &middot; {r.challenges_completed} challenges
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-[10px]" style={{ color: '#CCC' }}>
                        {new Date(r.generated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                      <a href={`${API_URL}/api/reports/school/${r.year}/${r.month}/download`}
                        onClick={(e) => {
                          e.preventDefault();
                          const token = getToken();
                          fetch(`${API_URL}/api/reports/school/${r.year}/${r.month}/download`, { headers: { Authorization: `Bearer ${token}` } })
                            .then(res => res.blob())
                            .then(blob => {
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement('a'); a.href = url; a.download = r.filename; a.click();
                              URL.revokeObjectURL(url);
                            });
                        }}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium text-white"
                        style={{ backgroundColor: '#F5831F' }}
                        data-testid={`download-report-${r.report_id}`}>
                        Download PDF
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Parent Reports Section */}
            <div className="bg-white rounded-xl p-5 mt-6" style={{ border: '1px solid #E8E8E8' }} data-testid="parent-reports-section">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-sm font-bold flex items-center gap-2" style={{ color: '#1A1209' }}>
                    <Mail size={14} style={{ color: '#2563EB' }} /> Parent Progress Reports
                  </h3>
                  <p className="text-[10px]" style={{ color: '#999' }}>Send engagement summaries to parents for each student.</p>
                </div>
                <button
                  onClick={async () => {
                    setSendingParentReports(true); setParentReportResult(null);
                    try {
                      const res = await fetch(`${API_URL}/api/school/reports/send-parent`, {
                        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
                        body: JSON.stringify({ send_all: true }),
                      });
                      if (res.ok) setParentReportResult(await res.json());
                    } catch (err) { /* handled */}
                    setSendingParentReports(false);
                  }}
                  disabled={sendingParentReports}
                  className="px-4 py-2 rounded-lg text-xs font-semibold text-white flex items-center gap-1.5 disabled:opacity-50"
                  style={{ backgroundColor: '#2563EB' }} data-testid="send-all-parent-reports"
                >
                  {sendingParentReports ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                  Send to All Parents
                </button>
              </div>
              {parentReportResult && (
                <div className="p-3 rounded-lg mb-3" style={{ backgroundColor: '#ECFDF5' }}>
                  <p className="text-xs font-semibold" style={{ color: '#065F46' }}>{parentReportResult.message}</p>
                </div>
              )}
              {parentReportHistory.length > 0 && (
                <div>
                  <h4 className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: '#999' }}>Recent Parent Reports</h4>
                  <div className="space-y-1.5">
                    {parentReportHistory.slice(0, 10).map(r => (
                      <div key={r.report_id} className="flex items-center justify-between px-3 py-2 rounded-lg" style={{ backgroundColor: '#F8F7F4' }}>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium" style={{ color: '#1A1209' }}>{r.student_name}</span>
                          <span className="text-[10px]" style={{ color: '#999' }}>{r.parent_email}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px]" style={{ color: '#999' }}>{r.period}</span>
                          <span className="px-1.5 py-0.5 rounded text-[9px] font-bold" style={{ backgroundColor: '#ECFDF5', color: '#059669' }}>Sent</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        {tab === 'messages' && (
          <div data-testid="messages-tab">
            <h1 className="text-xl font-bold mb-1" style={{ color: '#1A1209' }}>Contact HiBFF</h1>
            <p className="text-xs mb-5" style={{ color: '#999' }}>
              Send questions, suggestions, or technical queries directly to the HiBFF team. We&apos;ll respond within 24 hours.
            </p>

            {/* Compose form */}
            <div className="bg-white rounded-xl p-5 mb-5" style={{ border: '1px solid #E8E8E8' }} data-testid="message-form">
              <h3 className="text-sm font-bold mb-3" style={{ color: '#1A1209' }}>New Message</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="text-[10px] font-medium uppercase tracking-wider block mb-1" style={{ color: '#999' }}>Subject</label>
                  <input value={msgForm.subject} onChange={e => setMsgForm(p => ({ ...p, subject: e.target.value }))}
                    placeholder="What's this about?" className="w-full px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E8E8E8' }} data-testid="msg-subject" />
                </div>
                <div>
                  <label className="text-[10px] font-medium uppercase tracking-wider block mb-1" style={{ color: '#999' }}>Category</label>
                  <select value={msgForm.category} onChange={e => setMsgForm(p => ({ ...p, category: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E8E8E8' }} data-testid="msg-category">
                    <option value="general">General Enquiry</option>
                    <option value="technical">Technical Support</option>
                    <option value="billing">Billing / Subscription</option>
                    <option value="suggestion">Feature Suggestion</option>
                  </select>
                </div>
              </div>
              <div className="mb-3">
                <label className="text-[10px] font-medium uppercase tracking-wider block mb-1" style={{ color: '#999' }}>Message</label>
                <textarea value={msgForm.message} onChange={e => setMsgForm(p => ({ ...p, message: e.target.value }))}
                  placeholder="Describe your question or issue in detail..." rows={4}
                  className="w-full px-3 py-2 border rounded-lg text-sm resize-none" style={{ borderColor: '#E8E8E8' }} data-testid="msg-body" />
              </div>
              <button
                onClick={async () => {
                  if (!msgForm.subject || !msgForm.message) return;
                  setSendingMsg(true); setMsgSent(false);
                  try {
                    await fetch(`${API_URL}/api/school/messages/send`, {
                      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
                      body: JSON.stringify(msgForm),
                    });
                    setMsgSent(true);
                    setMsgForm({ subject: '', message: '', category: 'general' });
                    const r = await fetch(`${API_URL}/api/school/messages/my`, { headers: { Authorization: `Bearer ${getToken()}` } });
                    if (r.ok) { const d = await r.json(); setMessages(d.messages || []); }
                  } catch (err) { /* handled */}
                  setSendingMsg(false);
                }}
                disabled={!msgForm.subject || !msgForm.message || sendingMsg}
                className="w-full py-2.5 rounded-lg text-sm font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ backgroundColor: '#F5831F' }}
                data-testid="send-msg-btn"
              >
                {sendingMsg ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                {sendingMsg ? 'Sending...' : 'Send Message'}
              </button>
              {msgSent && (
                <div className="mt-3 p-3 rounded-lg text-center" style={{ backgroundColor: '#ECFDF5' }}>
                  <p className="text-xs font-semibold" style={{ color: '#065F46' }}>Message sent! The HiBFF team will respond within 24 hours.</p>
                </div>
              )}
            </div>

            {/* Message history */}
            {messages.length > 0 && (
              <div>
                <h3 className="text-sm font-bold mb-3 flex items-center gap-2" style={{ color: '#1A1209' }}>
                  <Clock size={14} style={{ color: '#999' }} /> Message History
                </h3>
                <div className="space-y-2">
                  {messages.map(m => {
                    const catColors = { general: '#2563EB', technical: '#8B5CF6', billing: '#F59E0B', suggestion: '#10B981' };
                    const catColor = catColors[m.category] || '#999';
                    return (
                      <div key={m.message_id} className="bg-white rounded-xl p-4" style={{ border: '1px solid #E8E8E8' }} data-testid={`msg-${m.message_id}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-0.5 rounded-full text-[9px] font-bold" style={{ backgroundColor: `${catColor}15`, color: catColor }}>
                              {m.category?.toUpperCase()}
                            </span>
                            <h4 className="text-sm font-bold" style={{ color: '#1A1209' }}>{m.subject}</h4>
                          </div>
                          <span className="flex items-center gap-1 text-[10px] font-semibold" style={{ color: m.status === 'replied' ? '#059669' : '#999' }}>
                            {m.status === 'replied' ? <Check size={10} /> : <Clock size={10} />}
                            {m.status === 'replied' ? 'Replied' : m.status === 'read' ? 'Read' : 'Sent'}
                          </span>
                        </div>
                        <p className="text-xs mb-2" style={{ color: '#666' }}>{m.message}</p>
                        <p className="text-[10px]" style={{ color: '#CCC' }}>
                          {m.created_at ? new Date(m.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                        </p>
                        {m.replies?.map((r, ri) => (
                          <div key={ri} className="mt-3 ml-4 pl-3 border-l-2" style={{ borderColor: '#F5831F' }}>
                            <p className="text-[10px] font-bold" style={{ color: '#F5831F' }}>HiBFF Team</p>
                            <p className="text-xs mt-1" style={{ color: '#333' }}>{r.message}</p>
                            <p className="text-[10px] mt-1" style={{ color: '#CCC' }}>
                              {r.created_at ? new Date(r.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                            </p>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {messages.length === 0 && !msgSent && (
              <div className="text-center py-8">
                <Mail size={32} style={{ color: '#DDD' }} className="mx-auto mb-3" />
                <p className="text-sm" style={{ color: '#999' }}>No messages yet. Use the form above to reach the HiBFF team.</p>
              </div>
            )}
          </div>
        )}

        {/* ── Settings ────────────────────────────────────────── */}
        {tab === 'settings' && (
          <div data-testid="settings-tab">
            <h1 className="text-xl font-bold mb-1" style={{ color: '#1A1209' }}>School Profile</h1>
            <p className="text-xs mb-5" style={{ color: '#999' }}>
              Manage your school&apos;s branding. The logo you upload will appear in the student dashboard header.
            </p>

            {/* Logo Upload Card */}
            <div className="bg-white rounded-xl p-6 mb-6" style={{ border: '1px solid #E8E8E8' }} data-testid="logo-upload-card">
              <h3 className="text-sm font-bold mb-4" style={{ color: '#1A1209' }}>School Logo</h3>

              {/* Current logo / upload area */}
              <div className="flex items-start gap-6 mb-5">
                <div className="w-24 h-24 rounded-xl border-2 border-dashed flex items-center justify-center overflow-hidden flex-shrink-0"
                  style={{ borderColor: '#E0E0E0', backgroundColor: '#F8F7F4' }}>
                  {(logoPreviewUrl || school?.logo_url) ? (
                    <img
                      src={logoPreviewUrl || `${API_URL}${school.logo_url}`}
                      alt="School logo"
                      className="max-w-full max-h-full object-contain"
                      data-testid="current-logo-img"
                    />
                  ) : (
                    <Image size={28} style={{ color: '#CCC' }} />
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-xs mb-2" style={{ color: '#888' }}>
                    Upload your school logo. Accepted formats: PNG, JPG, SVG. Maximum file size: 2MB.
                  </p>
                  <div className="flex gap-2">
                    <input
                      type="file"
                      ref={logoFileRef}
                      accept=".png,.jpg,.jpeg,.svg"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (file.size > 2 * 1024 * 1024) { alert('File exceeds 2MB limit'); return; }
                        setLogoFile(file);
                        setLogoPreviewUrl(URL.createObjectURL(file));
                      }}
                      data-testid="logo-file-input"
                    />
                    <button
                      onClick={() => logoFileRef.current?.click()}
                      className="px-3 py-2 rounded-lg text-xs font-medium flex items-center gap-1.5"
                      style={{ backgroundColor: '#F0F0F0', color: '#555' }}
                      data-testid="choose-logo-btn"
                    >
                      <Upload size={13} /> Choose File
                    </button>
                    {(logoPreviewUrl || school?.logo_url) && (
                      <button
                        onClick={async () => {
                          if (!window.confirm('Remove the school logo?')) return;
                          try {
                            await fetch(`${API_URL}/api/school/remove-logo`, {
                              method: 'DELETE',
                              headers: { Authorization: `Bearer ${getToken()}` },
                            });
                            setLogoPreviewUrl(null);
                            setLogoFile(null);
                            setSchool(prev => ({ ...prev, logo_url: null }));
                          } catch (err) { /* handled */ alert('Failed to remove logo'); }
                        }}
                        className="px-3 py-2 rounded-lg text-xs font-medium flex items-center gap-1.5"
                        style={{ backgroundColor: '#FEF2F2', color: '#DC2626' }}
                        data-testid="remove-logo-btn"
                      >
                        <Trash2 size={13} /> Remove
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Live Preview Strip */}
              {(logoPreviewUrl || school?.logo_url) && (
                <div className="rounded-xl p-4 mb-5" style={{ backgroundColor: '#1A1209' }} data-testid="logo-preview-strip">
                  <p className="text-[10px] font-medium mb-3 uppercase tracking-wider" style={{ color: '#A89880' }}>
                    Preview — how it appears in the student dashboard header
                  </p>
                  <div className="flex items-center gap-3" data-testid="logo-preview-content">
                    <img
                      src={logoPreviewUrl || `${API_URL}${school.logo_url}`}
                      alt="School logo preview"
                      style={{ height: 40, width: 'auto', maxWidth: 120, objectFit: 'contain' }}
                      data-testid="preview-school-logo"
                    />
                    <div style={{ width: 1, height: 32, backgroundColor: '#555' }} />
                    <img
                      src="/hibff-logo.png"
                      alt="HiBFF"
                      style={{ height: 28, width: 'auto', objectFit: 'contain' }}
                      data-testid="preview-hibff-logo"
                    />
                  </div>
                </div>
              )}

              {/* Save button (only when a new file is chosen) */}
              {logoFile && (
                <button
                  onClick={async () => {
                    setLogoUploading(true);
                    try {
                      const fd = new FormData();
                      fd.append('file', logoFile);
                      const res = await fetch(`${API_URL}/api/school/upload-logo`, {
                        method: 'POST',
                        headers: { Authorization: `Bearer ${getToken()}` },
                        body: fd,
                      });
                      const data = await res.json();
                      if (data.success) {
                        setSchool(prev => ({ ...prev, logo_url: data.logo_url }));
                        setLogoFile(null);
                        setLogoPreviewUrl(null);
                      } else {
                        alert(data.detail || 'Upload failed');
                      }
                    } catch (err) { /* handled */ alert('Upload failed'); }
                    setLogoUploading(false);
                  }}
                  disabled={logoUploading}
                  className="px-5 py-2.5 rounded-lg text-sm font-semibold text-white flex items-center gap-2 disabled:opacity-50"
                  style={{ backgroundColor: '#2EAA5C' }}
                  data-testid="save-logo-btn"
                >
                  {logoUploading ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                  {logoUploading ? 'Uploading...' : 'Save Logo'}
                </button>
              )}
            </div>

            {/* School Info (read-only) */}
            <div className="bg-white rounded-xl p-6" style={{ border: '1px solid #E8E8E8' }}>
              <h3 className="text-sm font-bold mb-4" style={{ color: '#1A1209' }}>School Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <InfoField label="School Name" value={school?.school_name} />
                <InfoField label="Type" value={school?.school_type} />
                <InfoField label="District" value={school?.school_district} />
                <InfoField label="State" value={school?.state} />
                <InfoField label="City" value={school?.city} />
                <InfoField label="ZIP" value={school?.zip_code} />
                <InfoField label="Contact" value={school?.contact_name} />
                <InfoField label="Email" value={school?.admin_email} />
              </div>
            </div>

            {/* Platform Preferences */}
            <div className="bg-white rounded-xl p-6" style={{ border: '1px solid #E8E8E8' }} data-testid="platform-prefs">
              <h3 className="text-sm font-bold mb-4" style={{ color: '#1A1209' }}>Platform Preferences</h3>

              {/* NFT Rewards Toggle */}
              <div className="flex items-center justify-between py-3 border-b" style={{ borderColor: '#F3F4F6' }} data-testid="nft-toggle-row">
                <div>
                  <p className="text-sm font-semibold" style={{ color: '#1A1209' }}>NFT Rewards</p>
                  <p className="text-[10px]" style={{ color: '#999' }}>
                    {schoolSettings.nft_rewards_enabled
                      ? 'Students earn blockchain-based NFT collectibles for achievements'
                      : 'NFT rewards are disabled — students earn traditional badges and points only'}
                  </p>
                </div>
                <button
                  onClick={async () => {
                    const newVal = !schoolSettings.nft_rewards_enabled;
                    setSchoolSettings(p => ({ ...p, nft_rewards_enabled: newVal }));
                    await fetch(`${API_URL}/api/school/settings`, {
                      method: 'PUT',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
                      body: JSON.stringify({ nft_rewards_enabled: newVal }),
                    });
                  }}
                  className="w-12 h-6 rounded-full transition-colors relative flex-shrink-0"
                  style={{ backgroundColor: schoolSettings.nft_rewards_enabled ? '#F5831F' : '#DDD' }}
                  data-testid="nft-toggle"
                >
                  <div className="w-5 h-5 bg-white rounded-full absolute top-0.5 transition-all shadow-sm"
                    style={{ left: schoolSettings.nft_rewards_enabled ? 26 : 2 }} />
                </button>
              </div>

              {/* Nudge Toggle */}
              <div className="flex items-center justify-between py-3 border-b" style={{ borderColor: '#F3F4F6' }} data-testid="nudge-toggle-row">
                <div>
                  <p className="text-sm font-semibold" style={{ color: '#1A1209' }}>Engagement Nudges</p>
                  <p className="text-[10px]" style={{ color: '#999' }}>Send automated reminders to inactive students</p>
                </div>
                <button
                  onClick={async () => {
                    const newVal = !schoolSettings.nudge_enabled;
                    setSchoolSettings(p => ({ ...p, nudge_enabled: newVal }));
                    await fetch(`${API_URL}/api/school/settings`, {
                      method: 'PUT',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
                      body: JSON.stringify({ nudge_enabled: newVal }),
                    });
                  }}
                  className="w-12 h-6 rounded-full transition-colors relative flex-shrink-0"
                  style={{ backgroundColor: schoolSettings.nudge_enabled ? '#2563EB' : '#DDD' }}
                  data-testid="nudge-toggle"
                >
                  <div className="w-5 h-5 bg-white rounded-full absolute top-0.5 transition-all shadow-sm"
                    style={{ left: schoolSettings.nudge_enabled ? 26 : 2 }} />
                </button>
              </div>

              {/* Nudge Threshold */}
              <div className="flex items-center justify-between py-3 border-b" style={{ borderColor: '#F3F4F6' }}>
                <div>
                  <p className="text-sm font-semibold" style={{ color: '#1A1209' }}>Inactive Threshold</p>
                  <p className="text-[10px]" style={{ color: '#999' }}>Days of inactivity before sending a nudge</p>
                </div>
                <select
                  value={schoolSettings.nudge_inactive_days}
                  onChange={async (e) => {
                    const val = parseInt(e.target.value);
                    setSchoolSettings(p => ({ ...p, nudge_inactive_days: val }));
                    await fetch(`${API_URL}/api/school/settings`, {
                      method: 'PUT',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
                      body: JSON.stringify({ nudge_inactive_days: val }),
                    });
                  }}
                  className="px-3 py-1.5 rounded-lg text-sm border"
                  style={{ borderColor: '#E8E8E8' }}
                  data-testid="nudge-days-select"
                >
                  {[1, 2, 3, 5, 7, 14].map(d => <option key={d} value={d}>{d} day{d > 1 ? 's' : ''}</option>)}
                </select>
              </div>

              {/* Max Daily Usage */}
              <div className="flex items-center justify-between py-3">
                <div>
                  <p className="text-sm font-semibold" style={{ color: '#1A1209' }}>Daily Usage Limit</p>
                  <p className="text-[10px]" style={{ color: '#999' }}>Recommended max minutes per day per student</p>
                </div>
                <select
                  value={schoolSettings.max_daily_usage_minutes}
                  onChange={async (e) => {
                    const val = parseInt(e.target.value);
                    setSchoolSettings(p => ({ ...p, max_daily_usage_minutes: val }));
                    await fetch(`${API_URL}/api/school/settings`, {
                      method: 'PUT',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
                      body: JSON.stringify({ max_daily_usage_minutes: val }),
                    });
                  }}
                  className="px-3 py-1.5 rounded-lg text-sm border"
                  style={{ borderColor: '#E8E8E8' }}
                  data-testid="daily-limit-select"
                >
                  {[15, 30, 45, 60, 90, 120].map(m => <option key={m} value={m}>{m} min</option>)}
                </select>
              </div>
            </div>

            {/* School Branding */}
            <div className="bg-white rounded-xl p-6 mt-6" style={{ border: '1px solid #E8E8E8' }} data-testid="branding-section">
              <h3 className="text-sm font-bold mb-4 flex items-center gap-2" style={{ color: '#1A1209' }}>
                <Palette size={14} style={{ color: '#8B5CF6' }} /> School Branding
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-medium uppercase tracking-wider block mb-1" style={{ color: '#999' }}>Primary Color</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={branding.primary_color || '#F5831F'}
                      onChange={e => setBranding(p => ({ ...p, primary_color: e.target.value }))}
                      className="w-10 h-10 rounded-lg border-0 cursor-pointer" data-testid="brand-color-picker" />
                    <span className="text-xs font-mono" style={{ color: '#666' }}>{branding.primary_color}</span>
                  </div>
                </div>
                <div>
                  <label className="text-[10px] font-medium uppercase tracking-wider block mb-1" style={{ color: '#999' }}>School Tagline</label>
                  <input value={branding.tagline || ''} onChange={e => setBranding(p => ({ ...p, tagline: e.target.value }))}
                    placeholder="e.g. Building Social Confidence Together"
                    className="w-full px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E8E8E8' }} data-testid="brand-tagline" />
                </div>
              </div>
              <button onClick={async () => {
                await fetch(`${API_URL}/api/school/branding`, {
                  method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
                  body: JSON.stringify(branding),
                });
                alert('Branding updated!');
              }} className="mt-4 px-4 py-2 rounded-lg text-xs font-semibold text-white" style={{ backgroundColor: '#8B5CF6' }} data-testid="save-branding-btn">
                Save Branding
              </button>
            </div>

            {/* Academic Calendar */}
            <div className="bg-white rounded-xl p-6 mt-6" style={{ border: '1px solid #E8E8E8' }} data-testid="calendar-section">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold flex items-center gap-2" style={{ color: '#1A1209' }}>
                  <Calendar size={14} style={{ color: '#2563EB' }} /> Academic Calendar
                </h3>
                <button onClick={() => setShowAddTerm(!showAddTerm)}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium text-white flex items-center gap-1"
                  style={{ backgroundColor: '#2563EB' }} data-testid="add-term-btn">
                  <Plus size={11} /> Add Period
                </button>
              </div>
              <p className="text-[10px] mb-3" style={{ color: '#999' }}>Set term dates and holidays. Nudges and inactivity alerts will auto-pause during holiday periods.</p>

              {showAddTerm && (
                <div className="p-3 rounded-lg mb-3" style={{ backgroundColor: '#F8F7F4', border: '1px solid #E8E8E8' }}>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
                    <input value={termForm.name} onChange={e => setTermForm(p => ({ ...p, name: e.target.value }))}
                      placeholder="Period name *" className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="term-name" />
                    <input type="date" value={termForm.start_date} onChange={e => setTermForm(p => ({ ...p, start_date: e.target.value }))}
                      className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="term-start" />
                    <input type="date" value={termForm.end_date} onChange={e => setTermForm(p => ({ ...p, end_date: e.target.value }))}
                      className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="term-end" />
                    <select value={termForm.type} onChange={e => setTermForm(p => ({ ...p, type: e.target.value }))}
                      className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="term-type">
                      <option value="term">Term</option>
                      <option value="holiday">Holiday</option>
                      <option value="exam">Exam Period</option>
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={async () => {
                      if (!termForm.name || !termForm.start_date || !termForm.end_date) return;
                      await fetch(`${API_URL}/api/school/calendar/terms`, {
                        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
                        body: JSON.stringify(termForm),
                      });
                      setTermForm({ name: '', start_date: '', end_date: '', type: 'term' });
                      setShowAddTerm(false);
                      fetchData();
                    }} className="px-3 py-1.5 rounded-lg text-xs font-medium text-white" style={{ backgroundColor: '#2EAA5C' }}>Save</button>
                    <button onClick={() => setShowAddTerm(false)} className="px-3 py-1.5 rounded-lg text-xs" style={{ backgroundColor: '#F0F0F0', color: '#666' }}>Cancel</button>
                  </div>
                </div>
              )}

              {calendarTerms.length > 0 ? (
                <div className="space-y-2">
                  {calendarTerms.map(t => {
                    const typeColors = { term: '#2563EB', holiday: '#F59E0B', exam: '#DC2626' };
                    const tc = typeColors[t.type] || '#999';
                    return (
                      <div key={t.term_id} className="flex items-center justify-between px-4 py-3 rounded-lg" style={{ backgroundColor: `${tc}08`, border: `1px solid ${tc}25` }} data-testid={`term-${t.term_id}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: tc }} />
                          <div>
                            <p className="text-sm font-semibold" style={{ color: '#1A1209' }}>{t.name}</p>
                            <p className="text-[10px]" style={{ color: '#999' }}>
                              {new Date(t.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} — {new Date(t.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-0.5 rounded text-[9px] font-bold capitalize" style={{ backgroundColor: `${tc}15`, color: tc }}>{t.type}</span>
                          <button onClick={async () => {
                            await fetch(`${API_URL}/api/school/calendar/terms/${t.term_id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${getToken()}` } });
                            fetchData();
                          }} className="p-1 rounded hover:bg-gray-100"><Trash2 size={12} style={{ color: '#DC2626' }} /></button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-center py-4" style={{ color: '#CCC' }}>No calendar periods set. Add your school terms and holidays.</p>
              )}
            </div>

            {/* Session Scheduling */}
            <div className="bg-white rounded-xl p-6 mt-6" style={{ border: '1px solid #E8E8E8' }} data-testid="schedule-section">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold flex items-center gap-2" style={{ color: '#1A1209' }}>
                  <Clock size={14} style={{ color: '#10B981' }} /> Session Schedule
                </h3>
                <button onClick={() => setShowAddSlot(!showAddSlot)}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium text-white flex items-center gap-1"
                  style={{ backgroundColor: '#10B981' }} data-testid="add-slot-btn">
                  <Plus size={11} /> Add Slot
                </button>
              </div>
              <p className="text-[10px] mb-3" style={{ color: '#999' }}>Define weekly practice windows for students. These appear in the student dashboard as scheduled sessions.</p>

              {showAddSlot && (
                <div className="p-3 rounded-lg mb-3" style={{ backgroundColor: '#F8F7F4', border: '1px solid #E8E8E8' }}>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-2">
                    <select value={slotForm.day} onChange={e => setSlotForm(p => ({ ...p, day: e.target.value }))}
                      className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="slot-day">
                      {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                    <input type="time" value={slotForm.start_time} onChange={e => setSlotForm(p => ({ ...p, start_time: e.target.value }))}
                      className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="slot-start" />
                    <input type="time" value={slotForm.end_time} onChange={e => setSlotForm(p => ({ ...p, end_time: e.target.value }))}
                      className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="slot-end" />
                    <select value={slotForm.grade} onChange={e => setSlotForm(p => ({ ...p, grade: e.target.value }))}
                      className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="slot-grade">
                      <option value="">All Grades</option>
                      {US_GRADES.map(g => <option key={g} value={g}>{g}</option>)}
                    </select>
                    <input value={slotForm.label} onChange={e => setSlotForm(p => ({ ...p, label: e.target.value }))}
                      placeholder="Label" className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="slot-label" />
                  </div>
                  <div className="flex gap-2">
                    <button onClick={async () => {
                      await fetch(`${API_URL}/api/school/schedule`, {
                        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
                        body: JSON.stringify(slotForm),
                      });
                      setSlotForm({ day: 'Monday', start_time: '09:00', end_time: '09:30', grade: '', label: 'Practice Session' });
                      setShowAddSlot(false);
                      fetchData();
                    }} className="px-3 py-1.5 rounded-lg text-xs font-medium text-white" style={{ backgroundColor: '#2EAA5C' }}>Save</button>
                    <button onClick={() => setShowAddSlot(false)} className="px-3 py-1.5 rounded-lg text-xs" style={{ backgroundColor: '#F0F0F0', color: '#666' }}>Cancel</button>
                  </div>
                </div>
              )}

              {scheduleSlots.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {scheduleSlots.map(s => (
                    <div key={s.slot_id} className="flex items-center justify-between px-3 py-2.5 rounded-lg" style={{ backgroundColor: '#F0FDF4', border: '1px solid #D1FAE5' }} data-testid={`slot-${s.slot_id}`}>
                      <div>
                        <p className="text-xs font-bold" style={{ color: '#065F46' }}>{s.day}</p>
                        <p className="text-[10px]" style={{ color: '#059669' }}>{s.start_time} — {s.end_time}</p>
                        <p className="text-[10px]" style={{ color: '#999' }}>{s.label} {s.grade ? `(${s.grade})` : '(All)'}</p>
                      </div>
                      <button onClick={async () => {
                        await fetch(`${API_URL}/api/school/schedule/${s.slot_id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${getToken()}` } });
                        fetchData();
                      }} className="p-1 rounded hover:bg-gray-100"><Trash2 size={12} style={{ color: '#DC2626' }} /></button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-center py-4" style={{ color: '#CCC' }}>No schedule slots. Add weekly practice sessions for your students.</p>
              )}
            </div>
          </div>
        )}
        {tab === 'guide' && (
          <div data-testid="guide-tab">
            <h1 className="text-xl font-bold mb-1" style={{ color: '#1A1209' }}>How-To Guide & FAQ</h1>
            <p className="text-xs mb-6" style={{ color: '#999' }}>
              Select a topic below to learn more about managing your school&apos;s HiBFF account.
            </p>

            {/* Section cards grid */}
            {!guideOpen._activeSection && guide?.sections && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="guide-sections-grid">
                {guide.sections.map((section, si) => {
                  const iconMap = { rocket: Zap, users: Users, chart: BarChart3, trophy: Trophy, shield: Shield, help: HelpCircle };
                  const Icon = iconMap[section.icon] || HelpCircle;
                  const colors = ['#F5831F', '#2563EB', '#10B981', '#8B5CF6', '#EF4444', '#F59E0B'];
                  const sectionColor = colors[si % colors.length];
                  return (
                    <button
                      key={si}
                      onClick={() => setGuideOpen({ _activeSection: si })}
                      className="bg-white rounded-2xl p-6 text-left transition-all hover:shadow-lg hover:-translate-y-0.5 group"
                      style={{ border: '2px solid #E8E8E8' }}
                      data-testid={`guide-section-${si}`}
                    >
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110"
                        style={{ backgroundColor: `${sectionColor}12` }}>
                        <Icon size={22} style={{ color: sectionColor }} />
                      </div>
                      <h3 className="text-base font-bold mb-1" style={{ color: '#1A1209' }}>{section.title}</h3>
                      <p className="text-xs" style={{ color: '#999' }}>{section.items.length} question{section.items.length !== 1 ? 's' : ''}</p>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Expanded section view */}
            {guideOpen._activeSection !== undefined && guideOpen._activeSection !== null && guide?.sections && (() => {
              const si = guideOpen._activeSection;
              const section = guide.sections[si];
              if (!section) return null;
              const iconMap = { rocket: Zap, users: Users, chart: BarChart3, trophy: Trophy, shield: Shield, help: HelpCircle };
              const Icon = iconMap[section.icon] || HelpCircle;
              const colors = ['#F5831F', '#2563EB', '#10B981', '#8B5CF6', '#EF4444', '#F59E0B'];
              const sectionColor = colors[si % colors.length];
              return (
                <div data-testid="guide-section-detail">
                  {/* Back button + section header */}
                  <button
                    onClick={() => setGuideOpen({})}
                    className="flex items-center gap-2 mb-5 text-sm font-semibold hover:opacity-70 transition-opacity"
                    style={{ color: sectionColor }}
                    data-testid="guide-back-btn"
                  >
                    <ChevronDown size={14} className="rotate-90" /> Back to all topics
                  </button>
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${sectionColor}12` }}>
                      <Icon size={20} style={{ color: sectionColor }} />
                    </div>
                    <h2 className="text-lg font-bold" style={{ color: '#1A1209' }}>{section.title}</h2>
                  </div>

                  {/* Q&A list */}
                  <div className="space-y-2">
                    {section.items.map((item, qi) => {
                      const qKey = `${si}-${qi}`;
                      const isOpen = guideOpen[qKey];
                      return (
                        <div key={qi} className="bg-white rounded-xl overflow-hidden transition-shadow"
                          style={{ border: isOpen ? `2px solid ${sectionColor}40` : '1px solid #E8E8E8' }}
                          data-testid={`guide-q-${si}-${qi}`}>
                          <button
                            onClick={() => setGuideOpen(p => ({ ...p, [qKey]: !p[qKey] }))}
                            className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors"
                          >
                            <span className="text-sm font-semibold pr-4" style={{ color: '#1A1209' }}>{item.q}</span>
                            {isOpen ? <ChevronUp size={16} style={{ color: sectionColor }} /> : <ChevronDown size={16} style={{ color: '#CCC' }} />}
                          </button>
                          {isOpen && (
                            <div className="px-5 pb-5 border-t" style={{ borderColor: '#F3F4F6' }}>
                              <p className="text-sm leading-relaxed pt-4" style={{ color: '#333', lineHeight: '1.7' }}>{item.a}</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {!guide && (
              <div className="bg-white rounded-xl p-8 text-center" style={{ border: '1px solid #E8E8E8' }}>
                <Loader2 size={24} className="animate-spin mx-auto mb-3" style={{ color: '#DDD' }} />
                <p className="text-sm" style={{ color: '#999' }}>Loading guide...</p>
              </div>
            )}
          </div>
        )}

        {tab === 'product-docs' && (
          <div>
            <h1 className="text-xl font-bold mb-4" style={{ color: '#1A1209' }}>Product Documentation</h1>
            <div className="bg-white rounded-xl p-6" style={{ border: '1px solid #E8E8E8' }}>
              <p className="text-sm text-gray-600 mb-4">Complete reference guides for all HiBFF products. These documents explain how each product works, who it&apos;s for, and the benefits for users.</p>
              <a href="/docs" className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-lg transition-colors" style={{ backgroundColor: '#4E6B52' }} data-testid="open-docs-btn">
                <FileText size={16} /> Open Product Documentation
              </a>
            </div>
          </div>
        )}
      </main>

      {/* Onboarding Wizard */}
      {showWizard && (
        <SchoolOnboardingWizard
          school={school}
          accessCodes={accessCodes}
          getToken={getToken}
          onComplete={(skipped) => {
            setShowWizard(false);
            setHasSkippedSteps(skipped.length > 0);
            fetchData();
            fetchStudents();
          }}
          onSchoolUpdate={setSchool}
          onStudentsUpdate={() => { fetchStudents(); fetchData(); }}
        />
      )}
    </div>
  );
};

/* ─── Sub-components ─────────────────────────────────────────────────────── */

const StatCard = ({ label, value, icon }) => (
  <div className="bg-white rounded-xl p-4" style={{ border: '1px solid #E8E8E8' }}>
    <div className="flex items-center justify-between mb-1">
      <span className="text-xs font-medium" style={{ color: '#999' }}>{label}</span>
      {icon}
    </div>
    <p className="text-2xl font-bold" style={{ color: '#1A1209' }}>{typeof value === 'number' ? value.toLocaleString() : value}</p>
  </div>
);

const DetailRow = ({ icon, label, value, valueColor }) => (
  <div className="flex items-center gap-3">
    <span style={{ color: '#BBB' }}>{icon}</span>
    <span className="text-xs w-28" style={{ color: '#999' }}>{label}</span>
    <span className="text-sm font-medium" style={{ color: valueColor || '#1A1209' }}>{value}</span>
  </div>
);

const TimelineStep = ({ num, text, active }) => (
  <li className="flex items-start gap-3">
    <div className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5"
      style={active ? { backgroundColor: '#2563EB', color: 'white' } : { backgroundColor: '#DBEAFE', color: '#93C5FD' }}>
      {num}
    </div>
    <span className="text-sm" style={{ color: active ? '#1E40AF' : '#93C5FD' }}>{text}</span>
  </li>
);

const InfoStep = ({ num, text }) => (
  <div className="flex items-start gap-3">
    <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0" style={{ backgroundColor: '#F5831F15', color: '#F5831F' }}>
      {num}
    </div>
    <p className="text-sm" style={{ color: '#666' }}>{text}</p>
  </div>
);

const InfoField = ({ label, value }) => (
  <div>
    <p className="text-[10px] font-medium uppercase tracking-wider mb-1" style={{ color: '#999' }}>{label}</p>
    <p className="text-sm font-medium" style={{ color: '#1A1209' }}>{value || '—'}</p>
  </div>
);

export default SchoolDashboard;
